import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  IconButton,
  Divider,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Switch,
  Checkbox,
  LinearProgress,
  DialogActions,
  useMediaQuery,
  useTheme,
  Alert,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import UpgradePlanModal from './UpgradePlanModal';
import PayAsYouNeedModal from './PayAsYouNeedModal';
import { useWithTokenRefresh } from '../utils/api';
import { useApi } from '../contexts/ApiContext';
import { CreditsTypes, OrganizationTypes, SettingsTypes } from '@flashbacktech/flashbackclient/api';
import GoogleAuthSetup from './mfa/GoogleAuthSetup';
import { getPlanTier, getPlanColor } from '../utils/subscriptionUtils';
import { formatCreditAmount } from '../utils/format';
import MagicLinkSetup from './mfa/MagicLinkSetup';
import PasskeySetup from './mfa/PasskeySetup';
import { MFATypes, SubscriptionTypes, QuotaTypes, DeviceTypes } from '@flashbacktech/flashbackclient/api';
import { useMFA } from '../contexts/MFAContext';
import CircularProgress from '@mui/material/CircularProgress';
import { toast } from 'react-toastify';

interface ProfileModalProps {
  open: boolean;
  onClose: () => void;
  initialSection?: 'Profile' | 'Organization' | 'Plan' | 'Billing' | 'Wallet' | 'Referrals';
  initialTab?: 'Personal' | 'Notifications' | 'Security';
  highlight2FA?: boolean;
}

const SidebarItem = styled(ListItem)(({ theme }) => ({
  padding: '8px 12px',
  borderRadius: '6px',
  width: '136px',
  height: '36px',
  gap: '8px',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    cursor: 'pointer',
  },
}));

const SidebarItemActive = styled(ListItem)(({ theme }) => ({
  padding: '8px 12px',
  borderRadius: '6px',
  width: '136px',
  height: '36px',
  gap: '8px',
  backgroundColor: '#FAFAFA',
  color: '#414651',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#FAFAFA',
    cursor: 'pointer',
  },
}));

const ProfileModal: React.FC<ProfileModalProps> = ({
  open,
  onClose,
  initialSection,
  initialTab,
  highlight2FA = false,
}) => {
  // Style commun pour tous les champs de saisie
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: '40px',
    },
  };

  const saveRoleChange = async () => {
    if (!profileUserId || pendingRole === '' || !authState?.token) return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.updateUserRole(profileUserId, Number(pendingRole)));
      if (res.success) {
        // Update local state on success
        setOrgRole(Number(pendingRole));
        setEditingRole(false);
      } else {
        throw new Error(res.message || 'Failed to update role');
      }
    } catch (e: any) {
      setProfileError(e?.message || 'Failed to update role');
    } finally {
      setProfileLoading(false);
    }
  };

  // Delete a device and reload list
  const handleDeleteDevice = async (deviceId: string) => {
    try {
      if (!window.confirm('Remove this device and revoke its sessions?')) return;
      setDevicesLoading(true);
      const res = await withTokenRefresh(() => apiClient.removeDevice(deviceId));
      if (res?.success) {
        await loadDevices();
      } else {
        setDevicesError(res?.message || 'Failed to remove device');
      }
    } catch (e: any) {
      setDevicesError(e?.message || 'Failed to remove device');
    } finally {
      setDevicesLoading(false);
    }
  };

  const { authState, globalData, refreshGlobalData } = useAuth();
  const { apiClient } = useApi();
  const user = authState.user;
  const [activeSection, setActiveSection] = useState('Profile');
  const [activeTab, setActiveTab] = useState('Personal');
  const [orgTab, setOrgTab] = useState<'Organization' | 'Team' | 'Workspace' | 'Plan' | 'Billing'>(
    'Organization'
  );
  const [upgradePlanModalOpen, setUpgradePlanModalOpen] = useState(false);
  const [addingMethod, setAddingMethod] = useState(false);
  const [payAsYouNeedOpen, setPayAsYouNeedOpen] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const withTokenRefresh = useWithTokenRefresh();

  // User profile state (Personal tab)
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileUserId, setProfileUserId] = useState<string>('');
  const [firstNameInput, setFirstNameInput] = useState<string>('');
  const [lastNameInput, setLastNameInput] = useState<string>('');
  const [emailInput, setEmailInput] = useState<string>('');
  // Track original profile values to detect changes
  const [originalFirstName, setOriginalFirstName] = useState<string>('');
  const [originalLastName, setOriginalLastName] = useState<string>('');
  const [hasUnsavedProfile, setHasUnsavedProfile] = useState<boolean>(false);

  // Security tab - Password change
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const [hasUnsavedPassword, setHasUnsavedPassword] = useState<boolean>(false);
  const [orgId, setOrgId] = useState<string>('');
  const [orgRole, setOrgRole] = useState<number | ''>('');
  const [orgRolesAvailable, setOrgRolesAvailable] = useState<number[]>([]);
  const [orgRoleDescription, setOrgRoleDescription] = useState<string>('');
  const [editingRole, setEditingRole] = useState(false);
  const [pendingRole, setPendingRole] = useState<number | ''>('');
  const allOrgRoles = React.useMemo(() => [0x00, 0x01, 0x02, 0xfe, 0xff], []);

  // Referrals - promo code and referral link states
  const [promoCodeInput, setPromoCodeInput] = useState<string>('');
  const [referralLink, setReferralLink] = useState<string>('');

  // Notifications (minimal local state; server is updated via partial PUT)
  const [emailNotifications, setEmailNotifications] = useState<boolean>(false);
  const [pushNotifications, setPushNotifications] = useState<boolean>(false);
  const [notifSaving, setNotifSaving] = useState<boolean>(false);
  const [notifError, setNotifError] = useState<string | null>(null);
  // Track original notification values to detect changes
  const [originalEmailNotifications, setOriginalEmailNotifications] = useState<boolean>(false);
  const [originalPushNotifications, setOriginalPushNotifications] = useState<boolean>(false);
  const [hasUnsavedNotifications, setHasUnsavedNotifications] = useState<boolean>(false);

  // Confirmation dialog for unsaved changes
  const [unsavedChangesDialog, setUnsavedChangesDialog] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);
  const [pendingSectionChange, setPendingSectionChange] = useState<string | null>(null);
  // Track MFA requirement from user profile
  const [userMfaRequired, setUserMfaRequired] = useState<boolean>(false);

  // Plan section - Subscriptions and quotas
  const [subscriptions, setSubscriptions] = useState<SubscriptionTypes.SubscriptionResponse[]>([]);
  const [currentSubscription, setCurrentSubscription] =
    useState<SubscriptionTypes.OrgSubscriptionResponse | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [selectedPeriodType, setSelectedPeriodType] = useState<QuotaTypes.PeriodType>(
    QuotaTypes.PeriodType.MONTHLY
  );
  const [bucketsCount, setBucketsCount] = useState(0);
  const [repositoriesCount, setRepositoriesCount] = useState(0);

  // Billing - Payments state
  const [payments, setPayments] = useState<SubscriptionTypes.PaymentResponse[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // Billing - Credit transactions and monthly stats (same as Organization page)
  const [planUsageTxPage, setPlanUsageTxPage] = useState(1);
  const [planUsageTxLimit] = useState(10);
  const [planUsageTxList, setPlanUsageTxList] = useState<Array<{
    id: string;
    creditAmount: number;
    createdAt: string;
    description: string | null;
    type: string;
    subscriptionName?: string;
    packName?: string;
  }>>([]);
  const [planUsageTxPagination, setPlanUsageTxPagination] = useState<{ total: number; page: number; limit: number } | null>(null);
  const [planUsageTxLoading, setPlanUsageTxLoading] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState<Array<{ month: string; consumption: number; purchases: number; grants: number; balance: number }>>([]);
  const [monthlyStatsTotals, setMonthlyStatsTotals] = useState<{ consumption: number; purchases: number; grants: number; balance: number } | null>(null);
  const [monthlyStatsLoading, setMonthlyStatsLoading] = useState(false);

  // Credits mode: when backend uses credits-based billing (set when getCreditsBalance succeeds)
  const [creditsMode, setCreditsMode] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState<{ realtimeBalance: number } | null>(null);

  // État pour les quotas depuis /user/quota
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [userQuota, setUserQuota] = useState<{
    subscription: any;
    quotaUsage: Array<{
      capability: {
        id: string;
        code: string;
        description: string;
        type: string;
        periodType: string;
      };
      usage: {
        current: number;
        max: number;
        percentage: number;
      };
    }>;
  } | null>(null);

  const defaultQuotaData = {
    storageUsed: 0,
    storageLimit: 0,
    storagePercentage: 0,
    subscriptionName: '',
    subscriptionType: '',
    displayLimit: '',
  };

  // État pour stocker les données de quota
  const [quotaData, setQuotaData] = useState<{
    storageUsed: number;
    storageLimit: number;
    storagePercentage: number;
    subscriptionName: string;
    subscriptionType: string;
    displayLimit: string;
  }>(() => {
    // Charger les données de quota depuis localStorage si elles existent
    const savedQuota = localStorage.getItem('quotaData');
    if (savedQuota) {
      try {
        const parsed = JSON.parse(savedQuota);
        if (parsed && typeof parsed === 'object' && typeof (parsed as { subscriptionName?: unknown }).subscriptionName === 'string') {
          return { ...defaultQuotaData, ...parsed };
        }
      } catch (error) { /* Ignore errors */ }
    }
    return defaultQuotaData;
  });

  // Hover details for Subscription card
  const [showSubDetails, setShowSubDetails] = useState(false);
  const subCardRef = React.useRef<HTMLDivElement | null>(null);
  const [subPanelPos, setSubPanelPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  // Helper functions for Plan section
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getQuotaByCode = (code: string) => {
    return userQuota?.quotaUsage?.find((q) => q.capability.code === code);
  };

  const formatPrice = (price: number): string => {
    return `$${price.toFixed(0)}`;
  };

  const formatPeriodType = (periodType: QuotaTypes.PeriodType): string => {
    switch (periodType) {
      case QuotaTypes.PeriodType.MONTHLY:
        return 'Monthly';
      case QuotaTypes.PeriodType.YEARLY:
        return 'Yearly';
      default:
        return periodType;
    }
  };

  const updateSubPanelPosition = React.useCallback(() => {
    const el = subCardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setSubPanelPos({
      top: Math.max(0, Math.round(rect.top - 10)),
      left: Math.round(rect.right + 16),
    });
  }, []);

  React.useEffect(() => {
    if (!showSubDetails) return;
    updateSubPanelPosition();
    const onResize = () => updateSubPanelPosition();
    const onScroll = () => updateSubPanelPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [showSubDetails, updateSubPanelPosition]);

  // Load functions for Plan section
  const loadUserQuota = React.useCallback(async () => {
    if (!authState?.token) return;
    setQuotaLoading(true);
    try {
      const quotaResp = await withTokenRefresh(() => apiClient.getUserQuota());
      if (quotaResp.success) {
        setUserQuota({
          subscription: quotaResp.subscription,
          quotaUsage: quotaResp.quotaUsage || [],
        });
      }
    } catch (error) { /* Ignore errors */ } finally {
      setQuotaLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  const loadSubscriptionsData = React.useCallback(async () => {
    if (!authState?.token) return;
    try {
      setSubscriptionLoading(true);
      const subsResp = await withTokenRefresh(() => apiClient.getSubscriptions());
      if (subsResp.success) {
        setSubscriptions(subsResp.data || []);
      }
      const mySubResp = await withTokenRefresh(() => apiClient.getMySubscription());
      if (mySubResp.success) {
        setCurrentSubscription(mySubResp.data);
        if (mySubResp.data?.periodType) {
          setSelectedPeriodType(mySubResp.data.periodType as QuotaTypes.PeriodType);
        }
      }
    } catch (error) { /* Ignore errors */ } finally {
      setSubscriptionLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  const loadBucketsCount = React.useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));
      if (response && response.buckets && Array.isArray(response.buckets)) {
        setBucketsCount(response.buckets.length);
      }
    } catch (error) { /* Ignore errors */ }
  }, [authState.token, withTokenRefresh, apiClient]);

  const loadRepositoriesCount = React.useCallback(async () => {
    try {
      if (!authState.token) return;
      const response = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));
      if (response && response.repos && Array.isArray(response.repos)) {
        setRepositoriesCount(response.repos.length);
      }
    } catch (error) { /* Ignore errors */ }
  }, [authState.token, withTokenRefresh, apiClient]);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) return;
    try {
      setSubscriptionLoading(true);
      const response = await withTokenRefresh(() => apiClient.cancelSubscription());
      if (response.success) {
        alert('Subscription cancelled successfully');
        await loadSubscriptionsData();
        await loadUserQuota();
      }
    } catch (error) {
      alert('Failed to cancel subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleBuySubscription = async (periodId: string) => {
    try {
      setSubscriptionLoading(true);
      const response = await withTokenRefresh(() =>
        apiClient.buySubscription({ subscriptionPeriodId: periodId })
      );
      if (response.success) {
        if (response.checkoutUrl) {
          // Paid subscription - open Stripe Checkout in a new window
          window.open(response.checkoutUrl, '_blank', 'noopener,noreferrer');
        } else {
          // Free subscription - checkoutUrl is undefined, refresh subscription status
          alert('Subscription purchased successfully!');
          await loadSubscriptionsData();
          await loadUserQuota();
        }
      } else {
        alert('Failed to process subscription. Please try again.');
      }
    } catch (error: any) {
      console.error('Error creating checkout session:', error);
      
      // Handle specific error cases
      if (error?.status === 409) {
        alert('You already have an active subscription. Please cancel your current subscription before upgrading to a new plan.');
      } else if (error?.data?.message) {
        alert(error.data.message);
      } else if (error?.message) {
        alert(error.message);
      } else {
        alert('Failed to start checkout. Please try again or contact support.');
      }
    } finally {
      setSubscriptionLoading(false);
    }
  };

  // Handle pending payment cancellation
  const handleCancelPendingPayment = async () => {
    if (!window.confirm('Are you sure you want to cancel this pending payment?')) return;
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.cancelPendingPayment());
      if (response.success) {
        // Reload payments to reflect the change
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to cancel pending payment');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Handle navigate to checkout for pending payment
  const handleNavigateToCheckout = async () => {
    try {
      setPaymentsLoading(true);
      const response = await withTokenRefresh(() => apiClient.getPendingPayment());
      if (response.success && response.data?.checkoutUrl) {
        // Navigate to the checkout URL
        window.open(response.data.checkoutUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Failed to get checkout URL. Please try again.');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to get checkout URL');
    } finally {
      setPaymentsLoading(false);
    }
  };

  // Open Stripe Billing Portal
  const handleOpenBillingPortal = async () => {
    try {
      const response = await withTokenRefresh(() => apiClient.createBillingPortal());
      if (response.success && response.url) {
        window.open(response.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error: any) {
      alert(error?.message || 'Failed to open billing portal');
    }
  };

  // Helper function to format payment status
  const formatPaymentStatus = (status: string) => {
    const config: Record<string, { label: string; bgcolor: string; color: string }> = {
      COMPLETED: { label: 'Paid', bgcolor: '#EAF8F1', color: '#1F8F6B' },
      FAILED: { label: 'Failed', bgcolor: '#FEE2E2', color: '#EF4444' },
      EXPIRED: { label: 'Expired', bgcolor: '#FEF3C7', color: '#F59E0B' },
      CANCELLED: { label: 'Cancelled', bgcolor: '#F3F4F6', color: '#6B7280' },
      PENDING: { label: 'Pending', bgcolor: '#FFF7E6', color: '#C38A00' },
    };
    return config[status] || config['PENDING'];
  };

  // Load Plan data when section is Plan
  React.useEffect(() => {
    if (open && activeSection === 'Plan') {
      loadUserQuota();
      loadSubscriptionsData();
      loadBucketsCount();
      loadRepositoriesCount();
    }
  }, [
    open,
    activeSection,
    loadUserQuota,
    loadSubscriptionsData,
    loadBucketsCount,
    loadRepositoriesCount,
  ]);


  // Load credit transactions and monthly stats when Billing section is active (same as Organization page)
  const loadPlanUsageTransactions = useCallback(async () => {
    console.log('[ProfileModal] loadPlanUsageTransactions called', { hasToken: !!authState?.token, page: planUsageTxPage, limit: planUsageTxLimit });
    if (!authState?.token) {
      console.log('[ProfileModal] loadPlanUsageTransactions: no token, returning early');
      return;
    }
    setPlanUsageTxLoading(true);
    try {
      console.log('[ProfileModal] loadPlanUsageTransactions: calling API...');
      const resp = await withTokenRefresh(() =>
        (apiClient as any).getCreditsTransactions({ page: planUsageTxPage, limit: planUsageTxLimit })
      ) as CreditsTypes.GetCreditsTransactionsResponse;
      console.log('[ProfileModal] loadPlanUsageTransactions: API response', resp);
      if (resp.success && resp.data) {
        setPlanUsageTxList(resp.data);
        setPlanUsageTxPagination(resp.pagination ?? null);
      }
    } catch (err) {
      console.error('[ProfileModal] loadPlanUsageTransactions: error', err);
      setPlanUsageTxList([]);
      setPlanUsageTxPagination(null);
    } finally {
      setPlanUsageTxLoading(false);
    }
  }, [authState?.token, planUsageTxPage, planUsageTxLimit, withTokenRefresh, apiClient]);

  const loadMonthlyStats = useCallback(async () => {
    console.log('[ProfileModal] loadMonthlyStats called', { hasToken: !!authState?.token });
    if (!authState?.token) {
      console.log('[ProfileModal] loadMonthlyStats: no token, returning early');
      return;
    }
    setMonthlyStatsLoading(true);
    try {
      console.log('[ProfileModal] loadMonthlyStats: calling V2 getCreditStats...');
      const resp = await withTokenRefresh(() =>
        apiClient.getCreditStats({ scope: 'org', window: '365d' } as any)
      ) as any;
      console.log('[ProfileModal] loadMonthlyStats: API response', resp);
      if (resp.success && resp.series?.length > 0) {
        const monthly = resp.series.map((point: any) => {
          const d = new Date(point.ts * 1000);
          return {
            month: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
            consumption: point.creditsTotal || 0,
            purchases: point.creditsPurchased || 0,
            grants: point.creditsGranted || 0,
            balance: (point.creditsPurchased || 0) + (point.creditsGranted || 0) - (point.creditsTotal || 0),
          };
        });
        setMonthlyStats(monthly);
        const totals = {
          consumption: monthly.reduce((sum: number, m: any) => sum + m.consumption, 0),
          purchases: monthly.reduce((sum: number, m: any) => sum + m.purchases, 0),
          grants: monthly.reduce((sum: number, m: any) => sum + m.grants, 0),
          balance: monthly.reduce((sum: number, m: any) => sum + m.balance, 0),
        };
        setMonthlyStatsTotals(totals);
      }
    } catch (err) {
      console.error('[ProfileModal] loadMonthlyStats: error', err);
      setMonthlyStats([]);
      setMonthlyStatsTotals(null);
    } finally {
      setMonthlyStatsLoading(false);
    }
  }, [authState?.token, withTokenRefresh, apiClient]);

  // Load credits balance + transactions + monthly stats in the same moment (Plan or Billing section)
  React.useEffect(() => {
    console.log('[ProfileModal] credits effect', { open, activeSection, hasToken: !!authState?.token });
    if (!open || (activeSection !== 'Plan' && activeSection !== 'Billing') || !authState?.token) {
      console.log('[ProfileModal] credits effect: conditions not met, skipping');
      return;
    }
    let mounted = true;
    (async () => {
      try {
        // Call balance, transactions, and stats together (balance sets creditsMode; others fill Plan Usage / Billing)
        console.log('[ProfileModal] credits effect: calling balance + transactions + stats...');
        const balancePromise = withTokenRefresh(() => (apiClient as any).getCreditsBalance()) as Promise<{ success: boolean; data?: { realtimeBalance: number } }>;
        loadPlanUsageTransactions();
        loadMonthlyStats();
        const balanceResp = await balancePromise;
        console.log('[ProfileModal] credits effect: balance response', balanceResp);
        if (mounted && balanceResp.success && balanceResp.data) {
          setCreditsMode(true);
          setCreditsBalance({ realtimeBalance: balanceResp.data.realtimeBalance });
        } else if (mounted) {
          setCreditsMode(false);
          setCreditsBalance(null);
        }
      } catch (err) {
        console.error('[ProfileModal] credits effect: error', err);
        if (mounted) {
          setCreditsMode(false);
          setCreditsBalance(null);
        }
      }
    })();
    return () => { mounted = false; };
  }, [open, activeSection, authState?.token, withTokenRefresh, apiClient, loadPlanUsageTransactions, loadMonthlyStats]);

  // Load payments when Billing section is active
  React.useEffect(() => {
    let mounted = true;
    const loadPaymentsData = async () => {
      if (!open || activeSection !== 'Billing' || !authState?.token) return;
      try {
        setPaymentsLoading(true);
        const paymentsResp = await withTokenRefresh(() => apiClient.getPayments());
        if (mounted && paymentsResp.success) {
          setPayments(paymentsResp.data || []);
        }
      } catch (error) { /* Ignore errors */ } finally {
        if (mounted) setPaymentsLoading(false);
      }
    };
    loadPaymentsData();
    return () => {
      mounted = false;
    };
  }, [open, activeSection, authState?.token, withTokenRefresh, apiClient]);

  // Reload transactions when page changes (balance effect already loads page 1 + stats)
  useEffect(() => {
    if (open && activeSection === 'Billing' && planUsageTxPage > 1) {
      loadPlanUsageTransactions();
    }
  }, [planUsageTxPage, open, activeSection, loadPlanUsageTransactions]);

  const saveNotificationSettings = async () => {
    if (!authState?.token) return;
    setNotifSaving(true);
    setNotifError(null);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.partialUpdateUserSettings({
          notifications: {
            email: emailNotifications,
            push: pushNotifications,
          },
        })
      );
      if (res.success) {
        // Update original values after successful save
        setOriginalEmailNotifications(emailNotifications);
        setOriginalPushNotifications(pushNotifications);
        setHasUnsavedNotifications(false);
      } else {
        throw new Error(res.message || 'Failed to update notifications');
      }
    } catch (e: any) {
      setNotifError(e?.message || 'Failed to update notifications');
    } finally {
      setNotifSaving(false);
    }
  };

  const partialUpdateUserSettings = async (payload: Record<string, any>) => {
    if (!authState?.token) return;
    setNotifSaving(true);
    setNotifError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.partialUpdateUserSettings(payload as SettingsTypes.PartialUpdateSettingsRequest));
      if (res.success) {
        // Refresh settings to get updated values
        try {
          const gdata = await withTokenRefresh(() => apiClient.getUserSettings());
          if (gdata.success && gdata.settings) {
            const settings = gdata.settings;
            const notif = settings.notifications || {};
            if (typeof notif.email === 'boolean') setEmailNotifications(notif.email);
            if (typeof notif.push === 'boolean') setPushNotifications(notif.push);
          }
        } catch {
          // ignore GET errors
        }
      } else {
        throw new Error(res.message || 'Failed to update notifications');
      }
    } catch (e: any) {
      setNotifError(e?.message || 'Failed to update notifications');
    } finally {
      setNotifSaving(false);
    }
  };

  // Load current notifications when opening the Notifications tab
  React.useEffect(() => {
    const shouldFetch =
      open && activeSection === 'Profile' && activeTab === 'Notifications' && !!authState?.token;
    if (!shouldFetch) return;
    let mounted = true;
    (async () => {
      try {
        const data = await withTokenRefresh(() => apiClient.getUserSettings());
        if (!mounted) return;
        if (data && data.success && data.settings) {
          const notif = data.settings.notifications || {};
          if (typeof notif.email === 'boolean') {
            setEmailNotifications(notif.email);
            setOriginalEmailNotifications(notif.email);
          }
          if (typeof notif.push === 'boolean') {
            setPushNotifications(notif.push);
            setOriginalPushNotifications(notif.push);
          }
        }
      } catch {
        // Silent: keep existing state
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, activeSection, activeTab, authState?.token, withTokenRefresh, apiClient]);

  // Detect changes in notifications to track unsaved state
  React.useEffect(() => {
    const hasChanges =
      emailNotifications !== originalEmailNotifications ||
      pushNotifications !== originalPushNotifications;
    setHasUnsavedNotifications(hasChanges);
  }, [
    emailNotifications,
    pushNotifications,
    originalEmailNotifications,
    originalPushNotifications,
  ]);

  // Detect changes in profile to track unsaved state
  React.useEffect(() => {
    const hasChanges =
      firstNameInput.trim() !== originalFirstName || lastNameInput.trim() !== originalLastName;
    setHasUnsavedProfile(hasChanges);
  }, [firstNameInput, lastNameInput, originalFirstName, originalLastName]);

  // Detect changes in password to track unsaved state
  React.useEffect(() => {
    const hasChanges =
      currentPassword.trim() !== '' || newPassword.trim() !== '' || confirmPassword.trim() !== '';
    setHasUnsavedPassword(hasChanges);
  }, [currentPassword, newPassword, confirmPassword]);

  // Handler for tab/section changes with unsaved check
  const handleTabChange = (newTab: string) => {
    if (hasUnsavedNotifications && activeTab === 'Notifications') {
      setPendingTabChange(newTab);
      setUnsavedChangesDialog(true);
    } else {
      setActiveTab(newTab);
    }
  };

  const handleSectionChange = (newSection: string) => {
    if (hasUnsavedNotifications && activeSection === 'Profile' && activeTab === 'Notifications') {
      setPendingSectionChange(newSection);
      setUnsavedChangesDialog(true);
    } else {
      setActiveSection(newSection);
      if (newSection === 'Profile') {
        setActiveTab('Personal');
      }
    }
  };

  const handleSaveAndContinue = async () => {
    await saveNotificationSettings();
    setUnsavedChangesDialog(false);
    if (pendingTabChange) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
    }
    if (pendingSectionChange) {
      setActiveSection(pendingSectionChange);
      setPendingSectionChange(null);
      if (pendingSectionChange === 'Profile') {
        setActiveTab('Personal');
      }
    }
  };

  const handleDiscardAndContinue = () => {
    // Revert to original values
    setEmailNotifications(originalEmailNotifications);
    setPushNotifications(originalPushNotifications);
    setHasUnsavedNotifications(false);
    setUnsavedChangesDialog(false);
    if (pendingTabChange) {
      setActiveTab(pendingTabChange);
      setPendingTabChange(null);
    }
    if (pendingSectionChange) {
      setActiveSection(pendingSectionChange);
      setPendingSectionChange(null);
      if (pendingSectionChange === 'Profile') {
        setActiveTab('Personal');
      }
    }
  };

  const handleCancelDialog = () => {
    setUnsavedChangesDialog(false);
    setPendingTabChange(null);
    setPendingSectionChange(null);
  };

  const handleCloseModal = () => {
    if (hasUnsavedNotifications && activeSection === 'Profile' && activeTab === 'Notifications') {
      // Show confirmation dialog
      const confirmLeave = window.confirm(
        'You have unsaved changes. Are you sure you want to close without saving?'
      );
      if (!confirmLeave) {
        return; // Don't close
      }
      // Reset notifications to original values
      setEmailNotifications(originalEmailNotifications);
      setPushNotifications(originalPushNotifications);
      setHasUnsavedNotifications(false);
    }
    onClose();
  };

  // Company (organization) state and effects
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>('');
  const [companyDomain, setCompanyDomain] = useState<string>('');
  const [companyAddress1, setCompanyAddress1] = useState<string>('');
  const [companyAddress2, setCompanyAddress2] = useState<string>('');
  const [companyCity, setCompanyCity] = useState<string>('');
  const [companyZipcode, setCompanyZipcode] = useState<string>('');
  const [companyState, setCompanyState] = useState<string>('');
  const [companyCountry, setCompanyCountry] = useState<string>('');
  const [companyPhone, setCompanyPhone] = useState<string>('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<string>('+1');
  const [companyWebsite, setCompanyWebsite] = useState<string>('');
  const [companyMfaEnforced, setCompanyMfaEnforced] = useState<boolean>(false);
  const canEditCompany = React.useMemo(() => orgRole === 0xfe || orgRole === 0xff, [orgRole]);

  // Load organization details when opening Organization hub
  React.useEffect(() => {
    const onOrgHub = activeSection === 'Organization' && orgTab === 'Organization';
    const shouldFetch = onOrgHub && !!authState?.token && !!orgId;
    if (!shouldFetch) return;
    let mounted = true;
    setCompanyError(null);
    setCompanyLoading(true);
    (async () => {
      try {
        const data = await withTokenRefresh(() => apiClient.getOrganization(orgId));
        if (!mounted) return;
        if (data.success && data.data) {
          const d = data.data as any;
          setCompanyName(d.name || '');
          setCompanyDomain(d.domain || '');
          setCompanyAddress1(d.address1 || '');
          setCompanyAddress2(d.address2 || '');
          setCompanyCity(d.city || '');
          setCompanyZipcode(d.zipcode || '');
          setCompanyState(d.state || '');
          setCompanyCountry(d.country || '');
          setCompanyWebsite(d.website || '');
          setCompanyMfaEnforced(d.mfaEnforced === true);
          if (d.phone) {
            const phoneMatch = d.phone.match(/^(\+\d{1,4})(.*)/);
            if (phoneMatch) {
              setPhoneCountryCode(phoneMatch[1]);
              setCompanyPhone(phoneMatch[2]);
            } else {
              setCompanyPhone(d.phone);
            }
          }
        } else {
          throw new Error(data.message || 'Failed to load organization');
        }
      } catch (e: any) {
        if (mounted) setCompanyError(e?.message || 'Failed to load organization');
      } finally {
        if (mounted) setCompanyLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [activeSection, activeTab, orgTab, authState?.token, orgId, withTokenRefresh, apiClient]);

  const handleSaveCompany = async () => {
    if (!authState?.token || !orgId || !canEditCompany) return;
    setCompanyLoading(true);
    setCompanyError(null);
    try {
      const fullPhone = companyPhone.trim() ? `${phoneCountryCode}${companyPhone.trim()}` : undefined;
      const body: any = {
        name: companyName.trim() || undefined,
        domain: companyDomain.trim() || undefined,
        address1: companyAddress1.trim() || undefined,
        address2: companyAddress2.trim() || undefined,
        city: companyCity.trim() || undefined,
        zipcode: companyZipcode.trim() || undefined,
        state: companyState.trim() || undefined,
        country: companyCountry.trim() || undefined,
        phone: fullPhone,
        website: companyWebsite.trim() || undefined,
        mfaEnforced: companyMfaEnforced,
      };

      const res = await withTokenRefresh(() => apiClient.updateOrganization(orgId, body));
      if (res.success) {
        await refreshGlobalData(true);
        if (res.data) {
          const d = res.data as any;
          setCompanyName(d.name || '');
          setCompanyDomain(d.domain || '');
          setCompanyAddress1(d.address1 || '');
          setCompanyAddress2(d.address2 || '');
          setCompanyCity(d.city || '');
          setCompanyZipcode(d.zipcode || '');
          setCompanyState(d.state || '');
          setCompanyCountry(d.country || '');
          setCompanyWebsite(d.website || '');
          setCompanyMfaEnforced(d.mfaEnforced === true);
          if (d.phone) {
            const phoneMatch = d.phone.match(/^(\+\d{1,4})(.*)/);
            if (phoneMatch) {
              setPhoneCountryCode(phoneMatch[1]);
              setCompanyPhone(phoneMatch[2]);
            } else {
              setCompanyPhone(d.phone);
            }
          }
        }
      } else {
        throw new Error(res.message || 'Failed to update organization');
      }
    } catch (e: any) {
      setCompanyError(e?.message || 'Failed to update organization');
    } finally {
      setCompanyLoading(false);
    }
  };

  // Use the same organization access logic as Sidebar (from AuthContext globalData)
  const hasOrgAccess = globalData.hasOrgAccess;

  // Persist org access flag for Sidebar to consume
  React.useEffect(() => {
    try {
      localStorage.setItem('hasOrgAccess', hasOrgAccess ? '1' : '0');
    } catch (_) {
      // ignore storage errors
    }
  }, [hasOrgAccess]);

  // Snackbar to notify when blocking Organization for individual accounts
  const [orgAccessWarning, setOrgAccessWarning] = useState(false);

  const roleLabel = (value: number) => {
    switch (value) {
      case 0x00:
        return 'USER';
      case 0x01:
        return 'BILLING';
      case 0x02:
        return 'WORKSPACES';
      case 0xfe:
        return 'ADMINISTRATORS';
      case 0xff:
        return 'OWNER';
      default:
        return `Role 0x${value.toString(16)}`;
    }
  };

  // Fetch profile when opening modal Personal tab
  React.useEffect(() => {
    const shouldFetch =
      open && activeSection === 'Profile' && activeTab === 'Personal' && !!authState?.token;
    if (!shouldFetch) return;
    let mounted = true;
    setProfileError(null);
    setProfileLoading(true);
    (async () => {
      try {
        // Use globalData.userProfile first (from AuthContext), only fetch if not available
        let profileData = null;
        if (globalData?.userProfile && globalData.isReady) {
          // Use cached data from globalData
          profileData = {
            success: true,
            data: {
              id: globalData.userProfile.email || '', // Use email as ID fallback
              name: globalData.userProfile.name || '',
              firstName: globalData.userProfile.firstName,
              lastName: globalData.userProfile.lastName,
              email: globalData.userProfile.email || '',
            }
          };
        } else {
          // Fallback to API call if globalData not ready
          profileData = await withTokenRefresh(() => apiClient.getUserProfile());
        }
        
        if (!mounted) return;
        if (profileData?.success && profileData?.data) {
          setProfileUserId(profileData.data.id || '');

          // Check if we have lastName separately
          const hasLastName = profileData.data.lastName;

          // Séparer le nom complet si firstName n'existe pas
          if (hasLastName) {
            // API returns firstName (or name) + lastName separately
            const firstName = profileData.data.name || '';
            const lastName = profileData.data.lastName || '';
            setFirstNameInput(firstName);
            setLastNameInput(lastName);
            setOriginalFirstName(firstName);
            setOriginalLastName(lastName);
          } else if (profileData.data.name) {
            // Si on a seulement 'name', essayer de le séparer
            const nameParts = (profileData.data.name || '').trim().split(' ');
            if (nameParts.length >= 2) {
              const lastName = nameParts.pop() || '';
              const firstName = nameParts.join(' ');
              setLastNameInput(lastName);
              setFirstNameInput(firstName);
              setOriginalFirstName(firstName);
              setOriginalLastName(lastName);
            } else {
              setFirstNameInput(profileData.data.name || '');
              setLastNameInput('');
              setOriginalFirstName(profileData.data.name || '');
              setOriginalLastName('');
            }
          }
          setEmailInput(profileData.data.email || '');
          if (typeof profileData.data.mfaRequired === 'boolean') setUserMfaRequired(!!profileData.data.mfaRequired);
          if (typeof profileData.data.orgId === 'string') setOrgId(profileData.data.orgId);
          if (typeof profileData.data.orgRole === 'number') setOrgRole(profileData.data.orgRole);
          if (Array.isArray(profileData.data.orgRoles)) setOrgRolesAvailable(profileData.data.orgRoles);
          setOrgRoleDescription(profileData.data.orgRoleDescription || '');
        } else {
          setProfileError(profileData?.message || profileData?.error || 'Failed to load profile');
        }
      } catch (e: any) {
        setProfileError(e?.message || 'Failed to load profile');
      } finally {
        setProfileLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [open, activeSection, activeTab, authState?.token, withTokenRefresh, apiClient]);

  const handleSaveProfile = async () => {
    if (!authState?.token || !profileUserId) return;

    // Validation
    if (!firstNameInput.trim() || !lastNameInput.trim()) {
      setProfileError('First name and last name are required');
      return;
    }

    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.updateUser(globalData.userProfile?.id || '', {
          name: firstNameInput.trim(),
          lastName: lastNameInput.trim(),
        })
      );
      if (!res.success) {
        throw new Error(res.message || 'Failed to update profile');
      }
      // Success - reload profile to confirm changes
      toast.success('Profile updated successfully!');

      // Refresh global data with forceRefresh to bypass cache (cache would otherwise
      // serve stale name/lastName for up to 10 min). This updates sidebar, AccountSettingsTutorial, etc.
      await refreshGlobalData(true);

      // Use the values we just submitted; globalData won't have updated yet in this closure
      // due to React's async state updates
      const newFirstName = firstNameInput.trim();
      const newLastName = lastNameInput.trim();
      setFirstNameInput(newFirstName);
      setLastNameInput(newLastName);
      setOriginalFirstName(newFirstName);
      setOriginalLastName(newLastName);
    } catch (e: any) {
      setProfileError(e?.message || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSavePassword = async () => {
    if (!authState?.token || !profileUserId) return;

    setPasswordError(null);

    // Validation
    if (!currentPassword.trim()) {
      setPasswordError('Current password is required');
      return;
    }

    if (!newPassword.trim()) {
      setPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    setPasswordLoading(true);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.updateUser(profileUserId, {
          password: newPassword,
        })
      );

      if (!res.success) {
        throw new Error(res.message || 'Failed to update password');
      }

      // Success
      alert('Password updated successfully!');

      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e: any) {
      setPasswordError(e?.message || 'Failed to update password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    if (!profileUserId || orgRole === '') return;
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.updateUserRole(profileUserId, Number(orgRole)));
      if (!res?.success) {
        setProfileError(res?.message || 'Failed to update role');
      }
    } catch (e: any) {
      setProfileError(e?.message || 'Failed to update role');
    } finally {
      setProfileLoading(false);
    }
  };

  // Detect SSO provider (google/github/microsoft) to hide password inputs in Security tab
  const isSSOLogin = React.useMemo(() => {
    try {
      const p = localStorage.getItem('loginProvider');
      return p === 'google' || p === 'github' || p === 'microsoft';
    } catch {
      return false;
    }
  }, [authState?.user]);

  // Organization members state
  const [team, setTeam] = useState<OrganizationTypes.OrgUserResponse[]>([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [canManageUsers, setCanManageUsers] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [invitePassword, setInvitePassword] = useState('');
  const [inviteRole, setInviteRole] = useState<number>(0);

  // Devices (Security > Browsers)
  const [devices, setDevices] = useState<DeviceTypes.DeviceItem[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);

  // MFA setup dialog state (for small cards in Security tab)
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [selectedMFAMethod, setSelectedMFAMethod] = useState<MFATypes.MFAType | null>(null);
  const {
    mfaState,
    setPrimaryMethod,
    disableMFAMethod,
    fetchMFAStatus,
    fetchAvailableMethods,
  } = useMFA();
  const [mfaDisableConfirmOpen, setMfaDisableConfirmOpen] = useState(false);
  const [mfaMethodToDisable, setMfaMethodToDisable] = useState<MFATypes.MFAType | null>(null);
  const [mfaActionLoading, setMfaActionLoading] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  // Local toggle for showing MFA Methods block (driven by globalData.userProfile.mfaRequired)
  const mfaRequiredFromProfile = globalData?.userProfile?.mfaRequired ?? userMfaRequired;
  const [twoFAEnabled, setTwoFAEnabled] = React.useState<boolean>(!!mfaRequiredFromProfile);
  // 2FA highlight state for reminder banner
  const [show2FAHighlight, setShow2FAHighlight] = React.useState(highlight2FA);
  const twoFARef = React.useRef<HTMLDivElement>(null);

  // Keep local toggle in sync with profile mfaRequired (globalData or fallback from profile fetch)
  React.useEffect(() => {
    setTwoFAEnabled(!!(globalData?.userProfile?.mfaRequired ?? userMfaRequired));
  }, [globalData?.userProfile?.mfaRequired, userMfaRequired]);

  // Scroll to 2FA section and highlight when highlight2FA prop is true
  // Highlight stays until user interacts with the 2FA toggle or closes the modal
  React.useEffect(() => {
    if (highlight2FA && open && activeSection === 'Profile' && activeTab === 'Security') {
      setShow2FAHighlight(true);
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        twoFARef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlight2FA, open, activeSection, activeTab]);

  // Clear highlight when modal closes
  React.useEffect(() => {
    if (!open) {
      setShow2FAHighlight(false);
    }
  }, [open]);

  const openMfaDialog = (method: MFATypes.MFAType) => {
    setSelectedMFAMethod(method);
    setMfaDialogOpen(true);
  };

  const closeMfaDialog = () => {
    setMfaDialogOpen(false);
    setSelectedMFAMethod(null);
  };

  const handleMFASetupComplete = () => {
    closeMfaDialog();
  };

  const handleSetMfaPrimary = async (mfaType: MFATypes.MFAType) => {
    setMfaActionLoading(true);
    try {
      await setPrimaryMethod(mfaType);
      await fetchMFAStatus();
    } finally {
      setMfaActionLoading(false);
    }
  };

  const handleDisableMfaClick = (mfaType: MFATypes.MFAType) => {
    setMfaMethodToDisable(mfaType);
    setMfaDisableConfirmOpen(true);
  };

  const handleDisableMfaConfirm = async () => {
    if (!mfaMethodToDisable) return;
    setMfaActionLoading(true);
    try {
      await disableMFAMethod(mfaMethodToDisable);
      await Promise.all([fetchMFAStatus(), fetchAvailableMethods()]);
      setMfaDisableConfirmOpen(false);
      setMfaMethodToDisable(null);
    } finally {
      setMfaActionLoading(false);
    }
  };

  const handleDisableMfaCancel = () => {
    setMfaDisableConfirmOpen(false);
    setMfaMethodToDisable(null);
  };

  // Helper to always get the latest token (post-refresh as well)
  const getAuthToken = React.useCallback(() => {
    if (authState?.token) return authState.token;
    try {
      const saved = localStorage.getItem('authState');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed?.token || null;
      }
    } catch (_) {
      // Fallback to null if localStorage is inaccessible or JSON parse fails
      return null;
    }
    return null;
  }, [authState?.token]);

  const loadTeam = React.useCallback(async () => {
    setTeamError(null);
    setTeamLoading(true);
    try {
      const res = await withTokenRefresh(() => apiClient.getOrganizationUsers());
      if (res?.success && Array.isArray(res.data)) {
        setTeam(res.data);
        setCanManageUsers(true);
      } else {
        setTeamError('Failed to load team');
      }
    } catch (e: any) {
      if (e?.status === 403) {
        setCanManageUsers(false);
        setTeamError(
          'Insufficient permissions to view team members (requires WORKSPACES role or higher).'
        );
      } else {
        setTeamError(e?.message || 'Failed to load team');
      }
    } finally {
      setTeamLoading(false);
    }
  }, [withTokenRefresh, apiClient]);

  const loadDevices = React.useCallback(async () => {
    setDevicesError(null);
    setDevicesLoading(true);
    try {
      const res = await withTokenRefresh(() => apiClient.getDevices());
      if (res?.success) {
        setDevices(res.devices || []);
      } else {
        setDevicesError('Failed to load devices');
      }
    } catch (e: any) {
      setDevicesError(e?.message || 'Failed to load devices');
    } finally {
      setDevicesLoading(false);
    }
  }, [withTokenRefresh, getAuthToken]);

  // Auto-load devices when switching to Security tab
  React.useEffect(() => {
    if (activeTab === 'Security') {
      loadDevices();
    }
  }, [activeTab, loadDevices]);

  const handleInvite = async () => {
    if (!inviteEmail || !inviteFirstName || !inviteLastName || !invitePassword) return;
    try {
      setTeamLoading(true);
      const res = await withTokenRefresh(() =>
        apiClient.createOrganizationUser({
          email: inviteEmail,
          password: invitePassword,
          firstName: inviteFirstName,
          lastName: inviteLastName,
          orgRole: inviteRole,
        })
      );
      if (res?.success) {
        setInviteEmail('');
        setInviteFirstName('');
        setInviteLastName('');
        setInvitePassword('');
        setInviteRole(0);
        setInviteOpen(false);
        await loadTeam();
      } else {
        setTeamError(res?.message || 'Failed to invite user');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to invite user');
    } finally {
      setTeamLoading(false);
    }
  };

  const handleDelete = async (userId: string) => {
    try {
      setTeamLoading(true);
      const res = await withTokenRefresh(() =>
        apiClient.deleteOrganizationUser(userId)
      );
      if (res?.success) {
        await loadTeam();
      } else {
        setTeamError(res?.message || 'Failed to remove user');
      }
    } catch (e: any) {
      setTeamError(e?.message || 'Failed to remove user');
    } finally {
      setTeamLoading(false);
    }
  };

  // Apply initial section when opening
  React.useEffect(() => {
    if (open && initialSection) {
      // If initial section is Organization, default to Profile since Organization is removed
      const next = String(initialSection) === 'Organization' ? 'Profile' : initialSection;
      setActiveSection(next);
    }
  }, [open, initialSection]);

  // Apply initial tab when opening
  React.useEffect(() => {
    if (open && initialTab) {
      setActiveTab(initialTab);
    }
    // If highlight2FA is true, ensure we're on Security tab
    if (open && highlight2FA) {
      setActiveSection('Profile');
      setActiveTab('Security');
    }
  }, [open, initialTab, highlight2FA]);

  // Set subscription type from quotaData
  React.useEffect(() => {
    if (quotaData && typeof quotaData.subscriptionType === 'string') {
      setSubscriptionType(quotaData.subscriptionType);
    }
  }, [quotaData]);

  if (!user) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth={false}
      disableScrollLock
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: '0', sm: '16px' },
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          height: { xs: '100vh', sm: '861px' },
          width: { xs: '100vw', sm: '712px' },
          maxWidth: '100%',
          m: { xs: 0, sm: 2 },
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            height: '100%',
            minHeight: 0,
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: '100%', sm: '184px' },
              borderRight: { xs: 'none', sm: '1px solid #eee' },
              borderBottom: { xs: '1px solid #eee', sm: 'none' },
              bgcolor: 'white',
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: 'center',
              justifyContent: 'space-between',
              overflowX: { xs: 'auto', sm: 'visible' },
              py: { xs: 1, sm: 0 },
              pt: { xs: 1, sm: 3 },
            }}
          >
            <List
              component="nav"
              aria-label="profile navigation"
              disablePadding
              sx={{ width: '136px' }}
            >
              {/* Personal tab in sidebar */}
              {activeTab === 'Personal' ? (
                <SidebarItemActive onClick={() => setActiveTab('Personal')}>
                  <ListItemText
                    primary="Personal"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#414651',
                      },
                    }}
                  />
                </SidebarItemActive>
              ) : (
                <SidebarItem onClick={() => setActiveTab('Personal')}>
                  <ListItemText
                    primary="Personal"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#717680',
                      },
                    }}
                  />
                </SidebarItem>
              )}

              {/* Notifications tab in sidebar */}
              {activeTab === 'Notifications' ? (
                <SidebarItemActive onClick={() => setActiveTab('Notifications')}>
                  <ListItemText
                    primary="Notifications"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#414651',
                      },
                    }}
                  />
                </SidebarItemActive>
              ) : (
                <SidebarItem onClick={() => setActiveTab('Notifications')}>
                  <ListItemText
                    primary="Notifications"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#717680',
                      },
                    }}
                  />
                </SidebarItem>
              )}

              {/* Security tab in sidebar */}
              {activeTab === 'Security' ? (
                <SidebarItemActive onClick={() => setActiveTab('Security')}>
                  <ListItemText
                    primary="Security"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#414651',
                      },
                    }}
                  />
                </SidebarItemActive>
              ) : (
                <SidebarItem onClick={() => setActiveTab('Security')}>
                  <ListItemText
                    primary="Security"
                    primaryTypographyProps={{
                      sx: {
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                        color: '#717680',
                      },
                    }}
                  />
                </SidebarItem>
              )}

              
              {/* Tutorial button - Available for all accounts */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E9EAEB' }}>
                <Box
                  onClick={() => {
                    onClose();
                    window.dispatchEvent(new CustomEvent('openAccountSettingsTutorial'));
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #F0F2FF 0%, #E8EBFF 100%)',
                    border: '1px solid #D4DAFF',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #E8EBFF 0%, #D4DAFF 100%)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(97, 123, 255, 0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      bgcolor: '#617BFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
                      <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/>
                      <circle cx="12" cy="17" r="0.5" fill="white"/>
                    </svg>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#414651', lineHeight: 1.3 }}>
                      Account Setup
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#717680', lineHeight: 1.3 }}>
                      2FA, Security & more
                    </Typography>
                  </Box>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#717680" strokeWidth="2">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </Box>
              </Box>
            </List>

            {/* Account type card at the bottom - Caché sur mobile */}
            <Box sx={{ width: '136px', pb: 2, display: { xs: 'none', sm: 'block' } }}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: '12px',
                  borderColor: '#E9EAEB',
                  bgcolor: '#FAFAFA',
                }}
              >
                <CardContent sx={{ p: 1.25, '&:last-child': { pb: 0.75 } }}>
                  {!hasOrgAccess && (
                    <>
                      <Typography
                        sx={{
                          fontSize: '11px',
                          color: '#717680',
                          mb: 0.5,
                          letterSpacing: 0.2,
                          textTransform: 'uppercase',
                        }}
                      >
                        Current Plan
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        {(() => {
                          const planTier = getPlanTier(quotaData?.subscriptionName);
                          const color = getPlanColor(quotaData?.subscriptionName);

                          return (
                            <>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: color,
                                }}
                              />
                              <Chip
                                label={planTier}
                                size="small"
                                sx={{
                                  height: 22,
                                  borderRadius: '999px',
                                  bgcolor: 'rgba(0,0,0,0.04)',
                                  color: '#414651',
                                  border: '1px solid',
                                  borderColor: '#E9EAEB',
                                  px: 1,
                                  fontWeight: 600,
                                  fontSize: '12px',
                                }}
                              />
                            </>
                          );
                        })()}
                      </Box>
                    </>
                  )}
                  <Typography
                    sx={{
                      fontSize: '11px',
                      color: '#717680',
                      mb: 0.5,
                      letterSpacing: 0.2,
                      textTransform: 'uppercase',
                    }}
                  >
                    Account type
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {(() => {
                      const accountType = hasOrgAccess ? 'Organization' : 'Individual';
                      const isOrg = accountType === 'Organization';
                      return (
                        <>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: isOrg ? '#617BFF' : '#A4A7AE',
                            }}
                          />
                          <Chip
                            label={accountType}
                            size="small"
                            sx={{
                              height: 22,
                              borderRadius: '999px',
                              bgcolor: isOrg ? 'rgba(97, 123, 255, 0.12)' : 'rgba(0,0,0,0.04)',
                              color: isOrg ? '#3B5BFF' : '#414651',
                              border: '1px solid',
                              borderColor: isOrg ? 'rgba(97, 123, 255, 0.35)' : '#E9EAEB',
                              px: 1,
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </>
                      );
                    })()}
                  </Box>
                  {/* Helper description */}
                  <Typography
                    sx={{ mt: 1, fontSize: '11px', lineHeight: '16px', color: '#717680' }}
                  >
                    {hasOrgAccess
                      ? 'You are part of an organization account.'
                      : 'This is an individual account.'}
                  </Typography>

                  {/* View button at bottom right */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setActiveSection('Plan')}
                      sx={{
                        textTransform: 'none',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#617BFF',
                        minWidth: 'auto',
                        px: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(97, 123, 255, 0.08)',
                        },
                      }}
                    >
                      View
                    </Button>
                  </Box>

                  {/* Snackbar removed: no save on Personal tab */}
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              bgcolor: 'white',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            {activeSection === 'Profile' && (
              <Box sx={{ position: 'relative' }}>
                {/* Account Information Badge */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '76px',
                    px: 4,
                    borderBottom: '1px solid #E9EAEB',
                    bgcolor: 'white',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '32px' }}
                  >
                    Profile
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CloseIcon sx={{ color: '#A4A7AE', strokeWidth: 1.67 }} />
                  </IconButton>
                </Box>

                {/* Dynamic content based on active tab - tabs now in sidebar */}
                <Box
                  sx={{
                    p: 0,
                    bgcolor: 'white',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    position: 'relative',
                  }}
                >
                  {activeTab === 'Personal' && (
                    <>
                      <Box
                        sx={{
                          overflowY: 'auto',
                          flex: 1,
                          mx: 'auto',
                          display: 'flex',
                          flexDirection: 'column',
                          p: { xs: 2, sm: 3 },
                          px: { xs: 2, sm: 3 },
                          mt: 3,
                          pb: 8,
                          width: { xs: '100%', sm: '480px' },
                          height: { xs: 'auto', sm: '486px' },
                          pr: 1,
                        }}
                      >
                        {profileError && (
                          <Alert severity="error" sx={{ mb: 2 }}>
                            {profileError}
                          </Alert>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ width: '48%' }}>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}
                            >
                              First name(s)<span style={{ color: '#617BFF' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={firstNameInput}
                              onChange={(e) => setFirstNameInput(e.target.value)}
                              placeholder="Enter your first name"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  color: '#414651',
                                  fontWeight: 500,
                                  '& fieldset': {
                                    borderColor: '#E9EAEB',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#617BFF',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#617BFF',
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ width: '48%' }}>
                            <Typography
                              sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}
                            >
                              Last name<span style={{ color: '#617BFF' }}>*</span>
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              value={lastNameInput}
                              onChange={(e) => setLastNameInput(e.target.value)}
                              placeholder="Enter your last name"
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '14px',
                                  color: '#414651',
                                  fontWeight: 500,
                                  '& fieldset': {
                                    borderColor: '#E9EAEB',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#617BFF',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#617BFF',
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 500, mb: 0.5, color: '#717680' }}
                          >
                            Email<span style={{ color: '#617BFF' }}>*</span>
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            value={emailInput}
                            disabled
                            variant="standard"
                            InputProps={{
                              readOnly: true,
                              disableUnderline: true,
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon sx={{ color: '#717680', fontSize: 20 }} />
                                </InputAdornment>
                              ),
                              sx: {
                                fontSize: '14px',
                                color: '#414651',
                                fontWeight: 500,
                                '&.Mui-disabled': {
                                  color: '#414651',
                                  WebkitTextFillColor: '#414651',
                                },
                              },
                            }}
                            sx={{
                              '& .MuiInput-root': {
                                '&:before': { display: 'none' },
                                '&:after': { display: 'none' },
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    </>
                  )}

                  {activeTab === 'Notifications' && (
                    <Box
                      sx={{
                        width: { xs: '100%', sm: '480px' },
                        height: { xs: 'auto', sm: '486px' },
                        mx: 'auto',
                        px: { xs: 2, sm: 0 },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        py: 3,
                      }}
                    >
                      {notifError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {notifError}
                        </Alert>
                      )}

                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#414651',
                              }}
                            >
                              Email notifications
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#535862',
                              }}
                            >
                              Receive important updates by email
                            </Typography>
                          </Box>
                          <Checkbox
                            checked={emailNotifications}
                            onChange={(e) => {
                              setEmailNotifications(e.target.checked);
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#414651',
                              }}
                            >
                              Push notifications
                            </Typography>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '20px',
                                color: '#535862',
                              }}
                            >
                              Receive push notifications in your browser
                            </Typography>
                          </Box>
                          <Checkbox
                            checked={pushNotifications}
                            onChange={(e) => {
                              setPushNotifications(e.target.checked);
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {activeTab === 'Security' && (
                    <Box
                      sx={{
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        overflow: 'auto',
                        height: 'calc(100vh - 180px)',
                      }}
                    >
                      {/* Password Fields (hidden for SSO accounts) */}
                      {!isSSOLogin ? (
                        <Box sx={{ mb: 4, maxWidth: '480px', width: '100%' }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                              mb: 1,
                            }}
                          >
                            Current password *
                          </Typography>
                          <TextField
                            type="password"
                            placeholder="••••••••"
                            fullWidth
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            error={!!passwordError}
                            sx={{
                              mb: 2,
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                borderWidth: '1px',
                                padding: '8px 12px',
                                color: '#414651',
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                              mb: 1,
                            }}
                          >
                            New password *
                          </Typography>
                          <TextField
                            type="password"
                            placeholder="••••••••"
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            error={!!passwordError}
                            sx={{
                              mb: 1,
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                borderWidth: '1px',
                                padding: '8px 12px',
                                color: '#414651',
                              },
                            }}
                          />

                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                              mb: 1,
                              mt: 2,
                            }}
                          >
                            Confirm password *
                          </Typography>
                          <TextField
                            type="password"
                            placeholder="••••••••"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            error={!!passwordError}
                            helperText={passwordError}
                            sx={{
                              mb: 1,
                              width: '100%',
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                                borderRadius: '8px',
                                borderWidth: '1px',
                                padding: '8px 12px',
                                color: '#414651',
                              },
                            }}
                          />

                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Box
                                sx={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  bgcolor: newPassword.length >= 8 ? '#10B981' : '#E9EAEB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                }}
                              >
                                <Typography
                                  sx={{ color: '#FFFFFF', fontSize: '10px', fontWeight: 600 }}
                                >
                                  ✓
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: newPassword.length >= 8 ? '#10B981' : '#535862',
                                }}
                              >
                                Must be at least 8 characters
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box
                                sx={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '50%',
                                  bgcolor: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
                                    ? '#10B981'
                                    : '#E9EAEB',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1,
                                }}
                              >
                                <Typography
                                  sx={{ color: '#FFFFFF', fontSize: '10px', fontWeight: 600 }}
                                >
                                  ✓
                                </Typography>
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 400,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
                                    ? '#10B981'
                                    : '#535862',
                                }}
                              >
                                Must contain one special character
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            mb: 4,
                            maxWidth: '480px',
                            width: '100%',
                            p: 2,
                            bgcolor: 'grey.50',
                            border: '1px solid #E9EAEB',
                            borderRadius: '8px',
                          }}
                        >
                          <Typography
                            sx={{ fontWeight: 600, fontSize: '14px', color: '#414651', mb: 0.5 }}
                          >
                            Password management unavailable
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#535862' }}>
                            Your account is connected with{' '}
                            {typeof window !== 'undefined'
                              ? (() => {
                                  const p = localStorage.getItem('loginProvider');
                                  if (p === 'google') return 'Google';
                                  if (p === 'github') return 'GitHub';
                                  if (p === 'microsoft') return 'Microsoft';
                                  return 'an SSO';
                                })()
                              : 'an SSO'}
                            . You cannot change a password here because SSO accounts do not have a
                            local password.
                          </Typography>
                        </Box>
                      )}

                      {/* Divider between Password and 2FA */}
                      <Divider sx={{ my: 2, borderColor: '#E9EAEB' }} />

                      {/* 2FA Toggle */}
                      <Box
                        ref={twoFARef}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 4,
                          p: show2FAHighlight ? 2 : 0,
                          borderRadius: show2FAHighlight ? '12px' : 0,
                          bgcolor: show2FAHighlight ? '#FEF3C7' : 'transparent',
                          border: show2FAHighlight ? '2px solid #F59E0B' : 'none',
                          transition: 'all 0.3s ease',
                          animation: show2FAHighlight ? 'pulse2FA 1s ease-in-out infinite' : 'none',
                          '@keyframes pulse2FA': {
                            '0%, 100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.4)' },
                            '50%': { boxShadow: '0 0 0 8px rgba(245, 158, 11, 0)' },
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                            }}
                          >
                            2-factor authentication (Recommended)
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#535862',
                            }}
                          >
                            Require a security key or code in addition to your password.
                          </Typography>
                        </Box>
                        <Switch
                          checked={twoFAEnabled}
                          onChange={(e) => {
                            setTwoFAEnabled(e.target.checked);
                            setShow2FAHighlight(false);
                          }}
                        />
                      </Box>

                      {/* MFA Methods quick actions */}
                      {twoFAEnabled && (
                        <Box sx={{ mb: 4 }}>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#414651',
                              mb: 1,
                            }}
                          >
                            Multi‑Factor Methods
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '20px',
                              color: '#535862',
                              mb: 2,
                            }}
                          >
                            Add or manage verification methods. You can configure more than one.
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 400,
                              fontSize: '13px',
                              lineHeight: '20px',
                              color: '#717680',
                              mb: 2,
                            }}
                          >
                            The <strong>default</strong> method is used first at login. You can change it or disable a method below.
                          </Typography>
                          <List
                            sx={{ maxWidth: 520, border: '1px solid #E9EAEB', borderRadius: '8px' }}
                          >
                            {(() => {
                              const methods = mfaState.methods ?? [];
                              const primaryMethod = mfaState.status?.primaryMethod;
                              console.log('@@@methods', methods);
                              const isGoogleAuthConfigured = methods.some(
                                (m) => m.type === MFATypes.MFAType.GOOGLE_AUTH && m.isConfigured && m.isEnabled
                              );
                              const isMagicLinkConfigured = methods.some(
                                (m) => m.type === MFATypes.MFAType.MAGIC_LINK && m.isConfigured && m.isEnabled
                              );
                              const isPasskeyConfigured = methods.some(
                                (m) => m.type === MFATypes.MFAType.PASSKEY && m.isConfigured && m.isEnabled
                              );
                              const configuredCount = [isGoogleAuthConfigured, isMagicLinkConfigured, isPasskeyConfigured].filter(Boolean).length;
                              const canDisableAny = configuredCount > 1;
                              const defaultChipSx = {
                                height: 24,
                                bgcolor: '#D1FAE5',
                                border: '1px solid #10B981',
                                color: '#059669',
                                fontWeight: 600,
                                fontSize: '12px',
                              };
                              const buttonSx = {
                                textTransform: 'none' as const,
                                borderRadius: '8px',
                                fontWeight: 600,
                                height: 36,
                                px: 1.5,
                                bgcolor: '#617BFF',
                                color: 'white',
                                '&:hover': { bgcolor: '#4A67E3' },
                              };
                              const textButtonSx = {
                                textTransform: 'none' as const,
                                fontWeight: 600,
                                fontSize: '12px',
                                minWidth: 'auto',
                                px: 0.5,
                              };
                              const renderSecondaryAction = (
                                configured: boolean,
                                isPrimary: boolean,
                                mfaType: MFATypes.MFAType
                              ) => {
                                if (!configured) {
                                  return (
                                    <Button
                                      variant="contained"
                                      sx={buttonSx}
                                      onClick={() => openMfaDialog(mfaType)}
                                      disabled={mfaActionLoading}
                                    >
                                      Set up
                                    </Button>
                                  );
                                }
                                if (isPrimary) {
                                  return (
                                    <Chip label="Default" size="small" sx={defaultChipSx} />
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Button
                                      variant="text"
                                      size="small"
                                      sx={{ ...textButtonSx, color: '#617BFF' }}
                                      onClick={() => handleSetMfaPrimary(mfaType)}
                                      disabled={mfaActionLoading}
                                    >
                                      Set as default
                                    </Button>
                                    <Typography sx={{ color: '#E9EAEB', fontSize: '12px' }}>·</Typography>
                                    <Button
                                      variant="text"
                                      size="small"
                                      sx={{ ...textButtonSx, color: '#717680' }}
                                      onClick={() => handleDisableMfaClick(mfaType)}
                                      disabled={mfaActionLoading || !canDisableAny}
                                    >
                                      Disable
                                    </Button>
                                  </Box>
                                );
                              };
                              return (
                                <>
                                  <ListItem
                                    secondaryAction={renderSecondaryAction(
                                      isGoogleAuthConfigured,
                                      primaryMethod === MFATypes.MFAType.GOOGLE_AUTH,
                                      MFATypes.MFAType.GOOGLE_AUTH
                                    )}
                                    sx={{ alignItems: 'flex-start', py: 1.25 }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          sx={{ fontWeight: 600, fontSize: '13px', color: '#181D27' }}
                                        >
                                          Google/MS Authenticator
                                          {primaryMethod === MFATypes.MFAType.GOOGLE_AUTH && (
                                            <Typography
                                              component="span"
                                              sx={{ ml: 0.5, fontSize: '12px', fontWeight: 500, color: '#10B981' }}
                                            >
                                              (Default)
                                            </Typography>
                                          )}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography sx={{ fontSize: '11px', color: '#717680' }}>
                                          Scan a QR code and use 6‑digit codes from the app.
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                  <Divider component="li" sx={{ borderColor: '#E9EAEB' }} />
                                  <ListItem
                                    secondaryAction={renderSecondaryAction(
                                      isMagicLinkConfigured,
                                      primaryMethod === MFATypes.MFAType.MAGIC_LINK,
                                      MFATypes.MFAType.MAGIC_LINK
                                    )}
                                    sx={{ alignItems: 'flex-start', py: 1.25 }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          sx={{ fontWeight: 600, fontSize: '13px', color: '#181D27' }}
                                        >
                                          Magic Link
                                          {primaryMethod === MFATypes.MFAType.MAGIC_LINK && (
                                            <Typography
                                              component="span"
                                              sx={{ ml: 0.5, fontSize: '12px', fontWeight: 500, color: '#10B981' }}
                                            >
                                              (Default)
                                            </Typography>
                                          )}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography sx={{ fontSize: '11px', color: '#717680' }}>
                                          Receive a secure link by email to verify your login.
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                  <Divider component="li" sx={{ borderColor: '#E9EAEB' }} />
                                  <ListItem
                                    secondaryAction={renderSecondaryAction(
                                      isPasskeyConfigured,
                                      primaryMethod === MFATypes.MFAType.PASSKEY,
                                      MFATypes.MFAType.PASSKEY
                                    )}
                                    sx={{ alignItems: 'flex-start', py: 1.25 }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Typography
                                          sx={{ fontWeight: 600, fontSize: '13px', color: '#181D27' }}
                                        >
                                          Passkey
                                          {primaryMethod === MFATypes.MFAType.PASSKEY && (
                                            <Typography
                                              component="span"
                                              sx={{ ml: 0.5, fontSize: '12px', fontWeight: 500, color: '#10B981' }}
                                            >
                                              (Default)
                                            </Typography>
                                          )}
                                        </Typography>
                                      }
                                      secondary={
                                        <Typography sx={{ fontSize: '11px', color: '#717680' }}>
                                          Use biometrics or device PIN to sign in.
                                        </Typography>
                                      }
                                    />
                                  </ListItem>
                                </>
                              );
                            })()}
                          </List>
                          {(() => {
                            const methods = mfaState.methods ?? [];
                            const c = [
                              methods.some((m) => m.type === MFATypes.MFAType.GOOGLE_AUTH && m.isEnabled && m.isConfigured),
                              methods.some((m) => m.type === MFATypes.MFAType.MAGIC_LINK && m.isEnabled && m.isConfigured),
                              methods.some((m) => m.type === MFATypes.MFAType.PASSKEY && m.isEnabled && m.isConfigured),
                            ].filter(Boolean).length;
                            return c === 1 ? (
                              <Typography sx={{ fontSize: '12px', color: '#9CA3AF', mt: 1 }}>
                                Add another method to disable this one.
                              </Typography>
                            ) : null;
                          })()}
                        </Box>
                      )}
                      {/* Browsers Section */}
                      <Box sx={{ mb: 4 }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#414651',
                            mb: 0.5,
                          }}
                        >
                          Browsers
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 400,
                            fontSize: '14px',
                            lineHeight: '20px',
                            color: '#535862',
                            mb: 1.5,
                          }}
                        >
                          These browsers are currently signed in to your account.
                        </Typography>
                        {devicesError && (
                          <Typography sx={{ color: '#ef4444', fontSize: '14px', mb: 1 }}>
                            {devicesError}
                          </Typography>
                        )}

                        {/* Browsers Card */}
                        <Card
                          sx={{
                            width: '100%',
                            minHeight: '120px',
                            borderRadius: '8px',
                            border: '1px solid #E9EAEB',
                            boxShadow: 'none',
                            mb: 4,
                          }}
                        >
                          <CardContent sx={{ p: 0 }}>
                            {/* Header Row */}
                            <Box
                              sx={{
                                display: 'flex',
                                borderBottom: '1px solid #E9EAEB',
                                p: 2,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: '#414651',
                                  width: '33%',
                                }}
                              >
                                Browser
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: '#414651',
                                  width: '33%',
                                }}
                              >
                                Location
                              </Typography>
                              <Typography
                                sx={{
                                  fontWeight: 500,
                                  fontSize: '14px',
                                  lineHeight: '20px',
                                  color: '#414651',
                                  width: '33%',
                                }}
                              >
                                Activity
                              </Typography>
                            </Box>
                            {devicesLoading && <LinearProgress />}
                            {!devicesLoading && devices.length === 0 && !devicesError && (
                              <Box sx={{ p: 2 }}>
                                <Typography sx={{ color: '#717680' }}>No devices found.</Typography>
                              </Box>
                            )}
                            {devices.map((d) => (
                              <Box
                                key={d.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  borderBottom: '1px solid #E9EAEB',
                                  p: 2,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#414651',
                                    width: '33%',
                                  }}
                                >
                                  {`${d.browser || 'Unknown'} on ${d.os || 'Unknown'}`}
                                  {d.deviceName ? ` — ${d.deviceName}` : ''}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontWeight: 400,
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    color: '#414651',
                                    width: '33%',
                                  }}
                                >
                                  {d.city || d.country
                                    ? `${d.city || ''}${d.city && d.country ? ', ' : ''}${
                                        d.country || ''
                                      }`
                                    : d.ipAddress || '—'}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '33%',
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 400,
                                      fontSize: '14px',
                                      lineHeight: '20px',
                                      color: '#414651',
                                    }}
                                  >
                                    {d.lastSeen ? new Date(d.lastSeen).toLocaleString() : '—'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {d.isTrusted && (
                                      <Chip label="Trusted" size="small" sx={{ height: 22 }} />
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteDevice(d.id)}
                                    >
                                      <DeleteOutlineIcon
                                        sx={{ color: '#A4A7AE', fontSize: '18px' }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Box>
                              </Box>
                            ))}
                          </CardContent>
                        </Card>

                        {/* Action Buttons */}

                        {/* Nested dialog for MFA setup */}
                        <Dialog
                          open={mfaDialogOpen}
                          onClose={(_, reason) => {
                            const inProgress = mfaState.isSettingUp || mfaState.isVerifying;
                            if (
                              inProgress &&
                              (reason === 'backdropClick' || reason === 'escapeKeyDown')
                            ) {
                              return;
                            }
                            closeMfaDialog();
                          }}
                          maxWidth="xs"
                          fullWidth
                          fullScreen={fullScreen}
                          keepMounted
                          scroll="paper"
                          disableEscapeKeyDown={mfaState.isSettingUp || mfaState.isVerifying}
                          TransitionComponent={Slide}
                          PaperProps={{
                            sx: {
                              maxHeight: 600,
                              maxWidth: '600px',
                            },
                          }}
                        >
                          <DialogTitle>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '16px',
                                  lineHeight: '24px',
                                  color: '#101828',
                                }}
                              >
                                {selectedMFAMethod === MFATypes.MFAType.GOOGLE_AUTH &&
                                  'Set up Google/MS Authenticator'}
                                {selectedMFAMethod === MFATypes.MFAType.MAGIC_LINK &&
                                  'Set up Magic Link'}
                                {selectedMFAMethod === MFATypes.MFAType.PASSKEY &&
                                  'Set up Passkey'}
                              </Typography>
                              <IconButton
                                onClick={closeMfaDialog}
                                disabled={mfaState.isSettingUp || mfaState.isVerifying}
                                aria-label="Close setup dialog"
                              >
                                <CloseIcon />
                              </IconButton>
                            </Box>
                          </DialogTitle>
                          <DialogContent dividers sx={{ p: 2 }}>
                            {selectedMFAMethod === MFATypes.MFAType.GOOGLE_AUTH && (
                              <Box
                                sx={{
                                  '& .MuiCardContent-root': { p: 2 },
                                  '& .MuiTypography-h6': { fontSize: 16 },
                                  '& .MuiTypography-subtitle2': { fontSize: 12 },
                                  '& .MuiTypography-body2': { fontSize: 12 },
                                  '& .MuiTypography-caption': { fontSize: 11 },
                                  '& .MuiAlert-root': { mb: 1.5 },
                                  '& .MuiGrid-container': { rowGap: 1 },
                                  '& .MuiPaper-root': { p: '10px !important' },
                                  '& .MuiPaper-root svg': { width: 120, height: 120 },
                                  '& .MuiTextField-root': { width: 160 },
                                }}
                              >
                                <GoogleAuthSetup onSetupComplete={handleMFASetupComplete} />
                              </Box>
                            )}
                            {selectedMFAMethod === MFATypes.MFAType.MAGIC_LINK && (
                              <Box sx={{ '& .MuiCardContent-root': { p: 2 } }}>
                                <MagicLinkSetup onSetupComplete={handleMFASetupComplete} />
                              </Box>
                            )}
                            {selectedMFAMethod === MFATypes.MFAType.PASSKEY && (
                              <Box sx={{ '& .MuiCardContent-root': { p: 2 } }}>
                                <PasskeySetup onSetupComplete={handleMFASetupComplete} />
                              </Box>
                            )}
                          </DialogContent>
                          <DialogActions>
                            <Button
                              onClick={closeMfaDialog}
                              disabled={mfaState.isSettingUp || mfaState.isVerifying}
                              sx={{ textTransform: 'none' }}
                            >
                              Close
                            </Button>
                          </DialogActions>
                        </Dialog>

                        {/* Confirm disable MFA method */}
                        <Dialog
                          open={mfaDisableConfirmOpen}
                          onClose={handleDisableMfaCancel}
                          maxWidth="xs"
                          fullWidth
                          PaperProps={{ sx: { borderRadius: '12px' } }}
                        >
                          <DialogTitle sx={{ fontWeight: 600, fontSize: '16px' }}>
                            Disable {mfaMethodToDisable === MFATypes.MFAType.GOOGLE_AUTH && 'Google/MS Authenticator'}
                            {mfaMethodToDisable === MFATypes.MFAType.MAGIC_LINK && 'Magic Link'}
                            {mfaMethodToDisable === MFATypes.MFAType.PASSKEY && 'Passkey'}?
                          </DialogTitle>
                          <DialogContent>
                            <Typography sx={{ fontSize: '14px', color: '#535862' }}>
                              This method will no longer be used for login. You can set it up again later. Make sure you have another method configured.
                            </Typography>
                          </DialogContent>
                          <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={handleDisableMfaCancel} sx={{ textTransform: 'none' }}>
                              Cancel
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={handleDisableMfaConfirm}
                              disabled={mfaActionLoading}
                              sx={{ textTransform: 'none' }}
                            >
                              {mfaActionLoading ? <CircularProgress size={20} /> : 'Disable'}
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Organization section removed */}

            {activeSection === 'Plan' && (
              <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative' }}>
                {/* Header with close button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '76px',
                    px: 4,
                    borderBottom: '1px solid #E9EAEB',
                    bgcolor: 'white',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '32px' }}
                  >
                    Plan
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CloseIcon sx={{ color: '#A4A7AE', strokeWidth: 1.67 }} />
                  </IconButton>
                </Box>
                {/* Content */}
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                {/* Loading indicator while fetching plan data */}
                {(quotaLoading || subscriptionLoading) && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress sx={{ borderRadius: '4px' }} />
                  </Box>
                )}
                {/* Current Subscription Status - Dedicated Section */}
                <Box sx={{ mb: 4 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#101828', mb: 2 }}>
                    Current Subscription
                  </Typography>
                  <Card
                    ref={subCardRef}
                    sx={{
                      borderRadius: '12px',
                      boxShadow: 'none',
                      bgcolor: 'white',
                      border: '2px solid #617BFF',
                      width: '468px',
                      minHeight: '120px',
                      position: 'relative',
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
                            {userQuota?.subscription?.name ||
                              currentSubscription?.name ||
                              'Free Trial'}
                          </Typography>
                          <Chip
                            label="Active"
                            size="small"
                            sx={{
                              height: 22,
                              bgcolor: '#10B981',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                          <Chip
                            label={currentSubscription?.periodType || 'YEARLY'}
                            size="small"
                            sx={{
                              height: 22,
                              borderRadius: '12px',
                              bgcolor: '#F2F4F7',
                              color: '#414651',
                              fontWeight: 600,
                              fontSize: '12px',
                            }}
                          />
                        </Box>
                        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#101828' }}>
                          {(() => {
                            // For free plans, show $0
                            const planName = (
                              userQuota?.subscription?.name ||
                              currentSubscription?.name ||
                              ''
                            ).toLowerCase();
                            const isFree = planName.includes('free') || planName.includes('trial');
                            const price = isFree ? 0 : currentSubscription?.price || 300;
                            return `$${price}`;
                          })()}
                          <Typography
                            component="span"
                            sx={{ fontSize: '14px', color: '#6B7280', ml: 0.5 }}
                          >
                            per {currentSubscription?.periodType === 'YEARLY' ? 'year' : 'month'}
                          </Typography>
                        </Typography>
                      </Box>

                      {currentSubscription?.dateFrom && currentSubscription?.dateTo && (
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '12px', color: '#717680', mb: 0.5 }}>
                            Subscription Period
                          </Typography>
                          <Typography sx={{ fontSize: '13px', color: '#535862' }}>
                            {new Date(currentSubscription.dateFrom).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}{' '}
                            -{' '}
                            {new Date(currentSubscription.dateTo).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </Typography>
                        </Box>
                      )}

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => setUpgradePlanModalOpen(true)}
                            sx={{
                              textTransform: 'none',
                              borderRadius: '8px',
                              backgroundColor: '#617BFF',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 600,
                              '&:hover': {
                                backgroundColor: '#5A6FE8',
                              },
                            }}
                          >
                            Upgrade
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                {/* Hover details panel for Subscription card */}
                {showSubDetails &&
                  (() => {
                    const currentPlan = subscriptions.find(
                      (s) => s.name === currentSubscription?.name
                    );
                    const currentPeriod = currentPlan?.periods?.find(
                      (p) => p.periodType === currentSubscription?.periodType
                    );

                    return (
                      <Box
                        sx={{
                          position: 'fixed',
                          zIndex: 9999,
                          left: subPanelPos.left,
                          top: subPanelPos.top,
                          width: '340px',
                          bgcolor: 'white',
                          border: '1px solid #E9EAEB',
                          borderRadius: '12px',
                          p: 2.5,
                          boxShadow: '0 8px 24px rgba(16,24,40,0.12)',
                          maxHeight: '600px',
                          overflowY: 'auto',
                        }}
                        onMouseEnter={() => setShowSubDetails(true)}
                        onMouseLeave={() => setShowSubDetails(false)}
                      >
                        <Typography
                          sx={{ fontSize: '16px', fontWeight: 700, color: '#101828', mb: 2 }}
                        >
                          Plan Usage
                        </Typography>

                        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                          <Typography
                            sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 0.5 }}
                          >
                            Current Plan
                          </Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>
                            {quotaData?.subscriptionName || currentPlan?.name || 'Business'}
                          </Typography>
                        </Box>

                        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                          <Typography
                            sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 1 }}
                          >
                            Resources
                          </Typography>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                              Buckets
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                            >
                              {(() => {
                                const bucketsQuota =
                                  getQuotaByCode('buckets.max') || getQuotaByCode('storage.units');
                                if (bucketsQuota) {
                                  return `${bucketsCount} / ${bucketsQuota.usage.max}`;
                                }
                                return `${bucketsCount} / Unlimited`;
                              })()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                              Repositories
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                            >
                              {(() => {
                                const reposQuota =
                                  getQuotaByCode('repos.max') || getQuotaByCode('repositories.max');
                                if (reposQuota) {
                                  return `${repositoriesCount} / ${reposQuota.usage.max}`;
                                }
                                return `${repositoriesCount} / Unlimited`;
                              })()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                              Private Bridge Nodes
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                            >
                              {(() => {
                                const nodesQuota =
                                  getQuotaByCode('nodes.max') ||
                                  getQuotaByCode('bridge.nodes') ||
                                  getQuotaByCode('nodes.private');
                                if (nodesQuota) {
                                  return `${nodesQuota.usage.current} / ${nodesQuota.usage.max}`;
                                }
                                return `0 / Unlimited`;
                              })()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography sx={{ fontSize: '12px', color: '#535862' }}>
                              Provider Integrations
                            </Typography>
                            <Typography
                              sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}
                            >
                              {(() => {
                                const providersQuota =
                                  getQuotaByCode('providers.max') ||
                                  getQuotaByCode('integrations.max');
                                const providers = 3;
                                if (providersQuota) {
                                  return `${providers} / ${providersQuota.usage.max}`;
                                }
                                return `${providers} / Unlimited`;
                              })()}
                            </Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography
                            sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 1 }}
                          >
                            Available Features
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(() => {
                              const features = (currentPeriod as any)?.features ||
                                (currentPlan as any)?.features || [
                                  'Public API',
                                  'Statistics',
                                  'Metrics',
                                  'Overview',
                                  'Advanced Filtering',
                                ];
                              return features.map((feature: string) => (
                                <Chip
                                  key={feature}
                                  label={feature}
                                  size="small"
                                  sx={{
                                    height: '22px',
                                    fontSize: '11px',
                                    bgcolor: '#F0FDF4',
                                    color: '#0E9F5D',
                                    border: '1px solid #D1FAE5',
                                    fontWeight: 500,
                                  }}
                                />
                              ));
                            })()}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })()}

                {/* Check if any limit is close to being exceeded */}
                {(() => {
                  const checkLimitExceeded = () => {
                    // Check buckets
                    const bucketsQuota = getQuotaByCode('buckets.max') || getQuotaByCode('storage.units');
                    if (bucketsQuota && bucketsQuota.usage.max > 0) {
                      const percentage = (bucketsCount / bucketsQuota.usage.max) * 100;
                      if (percentage >= 80) return { resource: 'Buckets', percentage, current: bucketsCount, max: bucketsQuota.usage.max };
                    }
                    
                    // Check repositories
                    const reposQuota = getQuotaByCode('repos.max') || getQuotaByCode('repositories.max');
                    if (reposQuota && reposQuota.usage.max > 0) {
                      const percentage = (repositoriesCount / reposQuota.usage.max) * 100;
                      if (percentage >= 80) return { resource: 'Repositories', percentage, current: repositoriesCount, max: reposQuota.usage.max };
                    }
                    
                    // Check nodes
                    const nodesQuota = getQuotaByCode('nodes.max') || getQuotaByCode('bridge.nodes') || getQuotaByCode('nodes.private');
                    if (nodesQuota && nodesQuota.usage.max > 0) {
                      const percentage = (nodesQuota.usage.current / nodesQuota.usage.max) * 100;
                      if (percentage >= 80) return { resource: 'Private Bridge Nodes', percentage, current: nodesQuota.usage.current, max: nodesQuota.usage.max };
                    }
                    
                    // Check team members
                    const usersQuota = getQuotaByCode('users.max') || getQuotaByCode('team.members');
                    if (usersQuota && usersQuota.usage.max > 0) {
                      const percentage = (usersQuota.usage.current / usersQuota.usage.max) * 100;
                      if (percentage >= 80) return { resource: 'Team Members', percentage, current: usersQuota.usage.current, max: usersQuota.usage.max };
                    }
                    
                    return null;
                  };
                  
                  const limitWarning = checkLimitExceeded();
                  
                  return limitWarning && (
                    <Alert 
                      severity="warning" 
                      sx={{ 
                        mb: 2, 
                        borderRadius: '12px',
                        border: '1px solid #FDB022',
                        bgcolor: '#FFFBEB',
                        width: '468px',
                        alignItems: 'center',
                        '& .MuiAlert-icon': {
                          color: '#F79009'
                        },
                        '& .MuiAlert-message': {
                          width: '100%',
                          py: 0.5
                        },
                        '& .MuiAlert-action': {
                          pt: 0,
                          pl: 2,
                          mr: 0
                        }
                      }}
                      action={
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setActiveSection('Plan')}
                          sx={{
                            bgcolor: '#617BFF',
                            color: 'white',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12px',
                            minWidth: 'auto',
                            px: 2,
                            py: 0.5,
                            height: '28px',
                            borderRadius: '6px',
                            boxShadow: 'none',
                            flexShrink: 0,
                            '&:hover': {
                              bgcolor: '#4A67E3',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          Upgrade
                        </Button>
                      }
                    >
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#92400E', mb: 0.5, lineHeight: 1.4 }}>
                          {limitWarning.percentage >= 100 ? 'Limit Reached' : 'Approaching Limit'}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#92400E', lineHeight: 1.5 }}>
                          You're using {limitWarning.current} of {limitWarning.max} {limitWarning.resource.toLowerCase()} ({Math.round(limitWarning.percentage)}%). {limitWarning.percentage >= 100 ? 'Upgrade to add more.' : 'Consider upgrading your plan.'}
                        </Typography>
                      </Box>
                    </Alert>
                  );
                })()}

                {/* Plan Usage Card - credits UI when credits mode, else legacy quota list */}
                <Card
                  sx={{
                    mb: 3,
                    borderRadius: '12px',
                    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                    bgcolor: 'white',
                    border: '1px solid #E9EAEB',
                    width: '468px',
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#101828', mb: 2 }}>
                      Plan Usage
                    </Typography>

                    {creditsMode ? (
                      <>
                        {/* Credits: Available balance */}
                        {creditsBalance != null && (
                          <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 0.5 }}>Available credits</Typography>
                            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>{creditsBalance.realtimeBalance.toLocaleString()} credits</Typography>
                          </Box>
                        )}
                        {/* Credit movements - font sizes match Organization list */}
                        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#717680', mb: 1 }}>Credit movements</Typography>
                          {planUsageTxLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                          ) : planUsageTxList.length === 0 ? (
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>No transactions yet</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {planUsageTxList.slice(0, 5).map((tx) => {
                                const desc = tx.description ?? (tx.subscriptionName ? `Subscription: ${tx.subscriptionName}` : tx.packName ? `Pack: ${tx.packName}` : '');
                                return (
                                  <Box key={tx.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                    <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      <Typography sx={{ fontSize: '12px', color: '#535862' }} noWrap>
                                        {new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · {tx.type}{desc ? ` · ${desc}` : ''}
                                      </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 500, color: tx.creditAmount >= 0 ? '#10B981' : '#101828', flexShrink: 0 }}>{tx.creditAmount >= 0 ? '+' : ''}{formatCreditAmount(tx.creditAmount)}</Typography>
                                  </Box>
                                );
                              })}
                              {planUsageTxPagination && planUsageTxPagination.total > planUsageTxLimit && (
                                <Typography sx={{ fontSize: '11px', color: '#717680', mt: 0.5 }}>+ {planUsageTxPagination.total - planUsageTxLimit} more</Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                        {/* Credits usage summary */}
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 1 }}>Credits usage (12 months)</Typography>
                          {monthlyStatsLoading ? (
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>Loading…</Typography>
                          ) : monthlyStatsTotals != null ? (
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box><Typography sx={{ fontSize: '11px', color: '#667085' }}>Consumption</Typography><Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#EF4444' }}>{monthlyStatsTotals.consumption.toLocaleString()}</Typography></Box>
                              <Box><Typography sx={{ fontSize: '11px', color: '#667085' }}>Purchases + Grants</Typography><Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#10B981' }}>{(monthlyStatsTotals.purchases + monthlyStatsTotals.grants).toLocaleString()}</Typography></Box>
                              <Box><Typography sx={{ fontSize: '11px', color: '#667085' }}>Net balance</Typography><Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#617BFF' }}>{monthlyStatsTotals.balance.toLocaleString()}</Typography></Box>
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>No usage data</Typography>
                          )}
                        </Box>
                        <Box sx={{ pt: 2, borderTop: '1px solid #E9EAEB', display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="contained" onClick={() => setUpgradePlanModalOpen(true)} endIcon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, py: 1, borderRadius: '8px', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', '&:hover': { bgcolor: '#4A67E3' } }}>Upgrade Plan</Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 0.5 }}>Current Plan</Typography>
                          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#101828' }}>{quotaData?.subscriptionName || 'Free'}</Typography>
                        </Box>
                        <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E9EAEB' }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 1 }}>Resources</Typography>
                          {(() => {
                            const bucketsQuota = getQuotaByCode('buckets.max') || getQuotaByCode('storage.units');
                            const reposQuota = getQuotaByCode('repos.max') || getQuotaByCode('repositories.max');
                            const nodesQuota = getQuotaByCode('nodes.max') || getQuotaByCode('bridge.nodes') || getQuotaByCode('nodes.private');
                            const usersQuota = getQuotaByCode('users.max') || getQuotaByCode('team.members');
                            const providersQuota = getQuotaByCode('providers.max') || getQuotaByCode('integrations.max');
                            const rows: Array<[string, string]> = [
                              ['Buckets', bucketsQuota ? `${bucketsCount} / ${bucketsQuota.usage.max}` : `${bucketsCount} / Unlimited`],
                              ['Repositories', reposQuota ? `${repositoriesCount} / ${reposQuota.usage.max}` : `${repositoriesCount} / Unlimited`],
                              ['Private Bridge Nodes', nodesQuota ? `${nodesQuota.usage.current} / ${nodesQuota.usage.max}` : '0 / Unlimited'],
                              ['Team Members', usersQuota ? `${usersQuota.usage.current} / ${usersQuota.usage.max}` : '1 / Unlimited'],
                              ['Provider Integrations', providersQuota ? `3 / ${providersQuota.usage.max}` : '3 / Unlimited'],
                            ];
                            return rows.map(([label, val]) => (
                              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                                <Typography sx={{ fontSize: '12px', color: '#535862' }}>{label}</Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#101828' }}>{val}</Typography>
                              </Box>
                            ));
                          })()}
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#717680', mb: 1 }}>Available Features</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {['Public API', 'Statistics', 'Metrics', 'Overview', 'Advanced Filtering'].map((feature: string) => (
                              <Chip key={feature} label={feature} size="small" sx={{ height: '22px', fontSize: '11px', bgcolor: '#F0FDF4', color: '#0E9F5D', border: '1px solid #D1FAE5', fontWeight: 500 }} />
                            ))}
                          </Box>
                        </Box>
                        <Box sx={{ pt: 2, borderTop: '1px solid #E9EAEB', display: 'flex', justifyContent: 'flex-end' }}>
                          <Button variant="contained" onClick={() => setUpgradePlanModalOpen(true)} endIcon={<svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 8h10M8 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} sx={{ bgcolor: '#617BFF', color: 'white', textTransform: 'none', fontWeight: 600, fontSize: '13px', px: 2.5, py: 1, borderRadius: '8px', boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)', '&:hover': { bgcolor: '#4A67E3' } }}>Upgrade Plan</Button>
                        </Box>
                      </>
                    )}

                  </CardContent>
                </Card>

                {/* Available Plans Section - Only show for Free users */}
                {quotaData?.subscriptionName && (quotaData.subscriptionName.toLowerCase().includes('free') || quotaData.subscriptionName.toLowerCase().includes('trial')) && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#101828', mb: 2 }}>
                        Available Plans
                      </Typography>

                      {/* Period Type Toggle */}
                      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            bgcolor: '#F3F4F6',
                            borderRadius: '8px',
                            padding: '4px',
                            gap: '4px',
                          }}
                        >
                      <Button
                        onClick={() => setSelectedPeriodType(QuotaTypes.PeriodType.MONTHLY)}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '14px',
                          bgcolor:
                            selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                              ? 'white'
                              : 'transparent',
                          color:
                            selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                              ? '#101828'
                              : '#6B7280',
                          boxShadow:
                            selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                              ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                              : 'none',
                          '&:hover': {
                            bgcolor:
                              selectedPeriodType === QuotaTypes.PeriodType.MONTHLY
                                ? 'white'
                                : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        Monthly
                      </Button>
                      <Button
                        onClick={() => setSelectedPeriodType(QuotaTypes.PeriodType.YEARLY)}
                        sx={{
                          px: 3,
                          py: 1,
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '14px',
                          bgcolor:
                            selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                              ? 'white'
                              : 'transparent',
                          color:
                            selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                              ? '#101828'
                              : '#6B7280',
                          boxShadow:
                            selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                              ? '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                              : 'none',
                          '&:hover': {
                            bgcolor:
                              selectedPeriodType === QuotaTypes.PeriodType.YEARLY
                                ? 'white'
                                : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        Yearly
                        <Chip
                          label="Save 20%"
                          size="small"
                          sx={{
                            ml: 1,
                            height: '20px',
                            fontSize: '11px',
                            bgcolor: '#10B981',
                            color: 'white',
                            fontWeight: 600,
                          }}
                        />
                      </Button>
                    </Box>
                  </Box>

                {/* Available Plans from API */}
                {subscriptionLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : subscriptions.length > 0 ? (
                  subscriptions.map((plan) => {
                    const selectedPeriod = plan.periods.find(
                      (p) => p.periodType === selectedPeriodType
                    );
                    const isCurrentPlanType = currentSubscription?.name === plan.name;
                    const isCurrentPlanPeriod =
                      currentSubscription?.periodType === selectedPeriodType;
                    const isExactCurrentPlan = isCurrentPlanType && isCurrentPlanPeriod;

                    return (
                      <Card
                        key={plan.id}
                        sx={{
                          mb: 2.5,
                          borderRadius: '12px',
                          boxShadow: 'none',
                          bgcolor: 'white',
                          border: isExactCurrentPlan ? '2px solid #E9EAEB' : '1px solid #E9EAEB',
                          width: '468px',
                          minHeight: '237px',
                          opacity: isExactCurrentPlan ? 0.6 : 1,
                        }}
                      >
                        <CardContent
                          sx={{ p: 3, pb: 2, height: '100%', display: 'flex', flexDirection: 'column', '&:last-child': { pb: 2 } }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '16px',
                                  lineHeight: '24px',
                                  color: '#101828',
                                }}
                              >
                                {plan.name}
                                {isExactCurrentPlan && (
                                  <Chip
                                    label="Current Plan"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      height: 20,
                                      bgcolor: '#F2F4F7',
                                      color: '#414651',
                                      fontWeight: 600,
                                      fontSize: '11px',
                                    }}
                                  />
                                )}
                              </Typography>
                              {selectedPeriod && (
                                <Chip
                                  label={formatPeriodType(
                                    selectedPeriod.periodType as QuotaTypes.PeriodType
                                  )}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    borderRadius: '12px',
                                    bgcolor: '#F2F4F7',
                                    color: '#414651',
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                            {selectedPeriod && (
                              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                <Typography
                                  sx={{ fontSize: '32px', fontWeight: 700, color: '#101828' }}
                                >
                                  {formatPrice(selectedPeriod.price)}
                                </Typography>
                                <Typography sx={{ color: '#6B7280' }}>
                                  per{' '}
                                  {selectedPeriod.periodType === QuotaTypes.PeriodType.MONTHLY
                                    ? 'month'
                                    : selectedPeriod.periodType === QuotaTypes.PeriodType.YEARLY
                                    ? 'year'
                                    : selectedPeriod.periodType.toLowerCase()}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Typography
                            sx={{ mt: 1, fontSize: '14px', lineHeight: '20px', color: '#535862' }}
                          >
                            {plan.description || 'Upgrade your capabilities'}
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            {isExactCurrentPlan ? (
                              <>
                                <Typography
                                  sx={{ color: '#6B7280', fontSize: '14px', fontWeight: 500 }}
                                >
                                  This is your current plan
                                </Typography>
                                <Button
                                  variant="outlined"
                                  onClick={() => setUpgradePlanModalOpen(true)}
                                  sx={{
                                    bgcolor: 'transparent',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    borderColor: '#E9EAEB',
                                    color: '#414651',
                                    '&:hover': {
                                      borderColor: '#617BFF',
                                      bgcolor: 'rgba(97, 123, 255, 0.04)',
                                    },
                                  }}
                                >
                                  Manage Plan
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  variant="text"
                                  sx={{
                                    color: '#414651',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    p: 0,
                                  }}
                                >
                                  Learn more
                                </Button>
                                {selectedPeriod && (
                                  <Button
                                    variant="contained"
                                    endIcon={
                                      subscriptionLoading ? (
                                        <CircularProgress size={16} color="inherit" />
                                      ) : (
                                        <ArrowForwardIcon />
                                      )
                                    }
                                    onClick={() => handleBuySubscription(selectedPeriod.id)}
                                    disabled={subscriptionLoading}
                                    sx={{
                                      bgcolor: '#617BFF',
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      borderRadius: '8px',
                                      '&:hover': { bgcolor: '#4A67E3' },
                                    }}
                                  >
                                    Subscribe
                                  </Button>
                                )}
                              </>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography sx={{ color: '#6B7280', fontSize: '14px', py: 4 }}>
                    No subscription plans available
                  </Typography>
                )}
                </Box>
              </>
            )}

                {/* COMMENTED OUT: Pay as you need section */}
                {false && (
                  <Card
                    sx={{
                      mb: 2.5,
                      borderRadius: '12px',
                      boxShadow: 'none',
                      bgcolor: 'white',
                      border: '1px solid #E9EAEB',
                      width: '468px',
                      height: '193px',
                    }}
                  >
                    <CardContent
                      sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#101828',
                        }}
                      >
                        Pay as you need
                      </Typography>
                      <Typography
                        sx={{ mt: 1, fontSize: '14px', lineHeight: '20px', color: '#535862' }}
                      >
                        Flashback can scale as you grow, offering a pay-as-you-need transfer capacity
                        option. Only pay for what you use.
                      </Typography>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          variant="text"
                          disabled
                          sx={{
                            color: '#A4A7AE',
                            textTransform: 'none',
                            fontWeight: 600,
                            p: 0,
                            '&.Mui-disabled': { color: '#A4A7AE' },
                          }}
                        >
                          Learn more
                        </Button>
                        <Button
                          variant="contained"
                          disabled
                          endIcon={<ArrowForwardIcon />}
                          sx={{
                            bgcolor: '#E9EAEB',
                            color: '#A4A7AE',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: '8px',
                            '&.Mui-disabled': { bgcolor: '#E9EAEB', color: '#A4A7AE' },
                          }}
                        >
                          Choose amount
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}
                </Box>
              </Box>
            )}

            {activeSection === 'Billing' && (
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '76px',
                    px: 4,
                    borderBottom: '1px solid #E9EAEB',
                    bgcolor: 'white',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '32px' }}
                  >
                    Billing
                  </Typography>
                  <IconButton
                    onClick={handleCloseModal}
                    sx={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <CloseIcon sx={{ color: '#A4A7AE', strokeWidth: 1.67 }} />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    p: 4,
                  }}
                >
                  {/* Manage Billing via Stripe Portal */}
                  <>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#101828',
                        }}
                      >
                        Billing Management
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleOpenBillingPortal}
                        sx={{
                          bgcolor: '#617BFF',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          '&:hover': { bgcolor: '#4A67E3' },
                        }}
                      >
                        Open Billing Portal
                      </Button>
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '16px',
                        lineHeight: '24px',
                        color: '#101828',
                        mt: 3,
                      }}
                    >
                      Billing history
                    </Typography>
                    <Card
                      sx={{
                        mb: 2.5,
                        borderRadius: '12px',
                        boxShadow: 'none',
                        bgcolor: 'white',
                        border: '1px solid #E9EAEB',
                        width: '468px',
                        minHeight: '260px',
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Box
                          sx={{
                            height: '44px',
                            bgcolor: '#FAFAFA',
                            borderBottom: '1px solid #E9EAEB',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Box sx={{ width: '140px' }}>
                            <Typography
                              sx={{ ml: 3, fontWeight: 500, fontSize: '12px', color: '#414651' }}
                            >
                              Date
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100px' }}>
                            <Typography
                              sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}
                            >
                              Status
                            </Typography>
                          </Box>
                          <Box sx={{ width: '100px', textAlign: 'right' }}>
                            <Typography
                              sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}
                            >
                              Amount
                            </Typography>
                          </Box>
                          <Box sx={{ width: '128px', textAlign: 'center' }}>
                            <Typography
                              sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}
                            >
                              Actions
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ minHeight: '216px', display: 'flex', flexDirection: 'column' }}>
                          {paymentsLoading ? (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 4,
                              }}
                            >
                              <CircularProgress size={24} />
                            </Box>
                          ) : payments.length > 0 ? (
                            payments
                              .slice(0, 10)
                              .sort(
                                (a, b) =>
                                  new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                              )
                              .map((payment, index) => {
                                const statusConfig = formatPaymentStatus(payment.status);
                                const isPending = payment.status === 'PENDING';
                                return (
                                  <Box
                                    key={payment.id}
                                    sx={{
                                      height: '48px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      borderBottom:
                                        index < Math.min(payments.length - 1, 9)
                                          ? '1px solid #E9EAEB'
                                          : 'none',
                                    }}
                                  >
                                    <Box sx={{ width: '140px' }}>
                                      <Typography
                                        sx={{ ml: 3, fontSize: '14px', color: '#414651' }}
                                      >
                                        {new Date(payment.timestamp).toLocaleDateString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                        })}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ width: '100px' }}>
                                      <Chip
                                        label={statusConfig.label}
                                        size="small"
                                        sx={{
                                          bgcolor: statusConfig.bgcolor,
                                          color: statusConfig.color,
                                        }}
                                      />
                                    </Box>
                                    <Box sx={{ width: '100px', textAlign: 'right' }}>
                                      <Typography sx={{ fontSize: '14px', color: '#414651' }}>
                                        ${payment.amount}
                                      </Typography>
                                    </Box>
                                    <Box sx={{ width: '128px', textAlign: 'center' }}>
                                      {isPending ? (
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            gap: 0.5,
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleNavigateToCheckout}
                                            disabled={paymentsLoading}
                                            sx={{
                                              fontSize: '11px',
                                              textTransform: 'none',
                                              borderColor: '#617BFF',
                                              color: '#617BFF',
                                              borderRadius: '6px',
                                              px: 1,
                                              py: 0.5,
                                              minWidth: 'auto',
                                              '&:hover': {
                                                borderColor: '#4A67E3',
                                                backgroundColor: 'rgba(97, 123, 255, 0.04)',
                                              },
                                            }}
                                          >
                                            Checkout
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={handleCancelPendingPayment}
                                            disabled={paymentsLoading}
                                            sx={{
                                              fontSize: '11px',
                                              textTransform: 'none',
                                              borderColor: '#EF4444',
                                              color: '#EF4444',
                                              borderRadius: '6px',
                                              px: 1,
                                              py: 0.5,
                                              minWidth: 'auto',
                                              '&:hover': {
                                                borderColor: '#DC2626',
                                                backgroundColor: 'rgba(239, 68, 68, 0.04)',
                                              },
                                            }}
                                          >
                                            Cancel
                                          </Button>
                                        </Box>
                                      ) : (
                                        <Typography sx={{ fontSize: '12px', color: '#717680' }}>
                                          -
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              })
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                py: 4,
                              }}
                            >
                              <Typography sx={{ fontSize: '14px', color: '#6B7280' }}>
                                No payment history available
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Credit movements (transactions) - font sizes match Organization list */}
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#101828', mt: 3, mb: 1 }}>
                      Credit movements
                    </Typography>
                    <Card sx={{ borderRadius: '12px', boxShadow: 'none', border: '1px solid #E9EAEB', width: '468px', mb: 2 }}>
                      <Box sx={{ height: 44, bgcolor: '#FAFAFA', borderBottom: '1px solid #E9EAEB', display: 'flex', alignItems: 'center', px: 2 }}>
                        <Box sx={{ width: 100 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Date</Typography></Box>
                        <Box sx={{ width: 80 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Type</Typography></Box>
                        <Box sx={{ flex: 1 }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Description</Typography></Box>
                        <Box sx={{ width: 64, textAlign: 'right' }}><Typography sx={{ fontWeight: 500, fontSize: '12px', color: '#414651' }}>Amount</Typography></Box>
                      </Box>
                      <Box sx={{ minHeight: 80 }}>
                        {planUsageTxLoading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : planUsageTxList.length === 0 ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                            <Typography sx={{ fontSize: '12px', color: '#6B7280' }}>No transactions yet</Typography>
                          </Box>
                        ) : (
                          planUsageTxList.map((tx) => (
                            <Box key={tx.id} sx={{ minHeight: 40, display: 'flex', alignItems: 'center', px: 2, py: 1, borderBottom: '1px solid #E9EAEB' }}>
                              <Box sx={{ width: 100 }}><Typography sx={{ fontSize: '12px', color: '#414651' }}>{new Date(tx.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Typography></Box>
                              <Box sx={{ width: 80 }}><Typography sx={{ fontSize: '12px', color: '#535862' }}>{tx.type}</Typography></Box>
                              <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}><Typography sx={{ fontSize: '12px', color: '#414651' }} noWrap>{tx.description ?? (tx.subscriptionName ? `Subscription: ${tx.subscriptionName}` : tx.packName ? `Pack: ${tx.packName}` : '—')}</Typography></Box>
                              <Box sx={{ width: 64, textAlign: 'right' }}><Typography sx={{ fontSize: '12px', fontWeight: 500, color: tx.creditAmount >= 0 ? '#10B981' : '#101828' }}>{tx.creditAmount >= 0 ? '+' : ''}{formatCreditAmount(tx.creditAmount)}</Typography></Box>
                            </Box>
                          ))
                        )}
                      </Box>
                      {planUsageTxPagination && planUsageTxPagination.total > planUsageTxLimit && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1, borderTop: '1px solid #E9EAEB', bgcolor: '#FAFAFA' }}>
                          <Typography sx={{ fontSize: '12px', color: '#535862' }}>Page {planUsageTxPagination.page} of {Math.ceil(planUsageTxPagination.total / planUsageTxPagination.limit)}</Typography>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" disabled={planUsageTxPage <= 1 || planUsageTxLoading} onClick={() => setPlanUsageTxPage((p) => Math.max(1, p - 1))} sx={{ textTransform: 'none' }}>Previous</Button>
                            <Button size="small" disabled={planUsageTxLoading || planUsageTxPage >= Math.ceil(planUsageTxPagination.total / planUsageTxPagination.limit)} onClick={() => setPlanUsageTxPage((p) => p + 1)} sx={{ textTransform: 'none' }}>Next</Button>
                          </Box>
                        </Box>
                      )}
                    </Card>

                    {/* Credits usage (monthly stats) */}
                    <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#101828', mb: 1 }}>
                      Credits usage (last 12 months)
                    </Typography>
                    {monthlyStatsLoading ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}><CircularProgress size={20} /><Typography sx={{ fontSize: '14px', color: '#6B7280' }}>Loading…</Typography></Box>
                    ) : monthlyStatsTotals != null ? (
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 2 }}>
                        <Box><Typography sx={{ fontSize: '12px', color: '#667085' }}>Consumption</Typography><Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#EF4444' }}>{monthlyStatsTotals.consumption.toLocaleString()}</Typography></Box>
                        <Box><Typography sx={{ fontSize: '12px', color: '#667085' }}>Purchases + Grants</Typography><Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#10B981' }}>{(monthlyStatsTotals.purchases + monthlyStatsTotals.grants).toLocaleString()}</Typography></Box>
                        <Box><Typography sx={{ fontSize: '12px', color: '#667085' }}>Net balance</Typography><Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#617BFF' }}>{monthlyStatsTotals.balance.toLocaleString()}</Typography></Box>
                      </Box>
                    ) : (
                      <Typography sx={{ fontSize: '14px', color: '#6B7280', mb: 2 }}>No usage data</Typography>
                    )}
                  </>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Footer for Profile tabs only - always at bottom of modal */}
        {activeSection === 'Profile' &&
          (activeTab === 'Personal' ||
            activeTab === 'Notifications' ||
            activeTab === 'Security') && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 2, sm: 0 },
                borderTop: '1px solid #E9EAEB',
                bgcolor: 'white',
                px: { xs: 2, sm: 3 },
                py: 2,
              }}
            >
              <Button
                sx={{
                  color: '#F44336',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '13px', sm: '14px' },
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'center', sm: 'flex-start' },
                }}
              >
                Delete account
              </Button>
              <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (activeTab === 'Personal' && hasUnsavedProfile) {
                      // Reset profile to original values
                      setFirstNameInput(originalFirstName);
                      setLastNameInput(originalLastName);
                    } else if (activeTab === 'Security' && hasUnsavedPassword) {
                      // Reset password fields
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setPasswordError(null);
                    } else if (activeTab === 'Notifications' && hasUnsavedNotifications) {
                      // Reset notifications to original values
                      setEmailNotifications(originalEmailNotifications);
                      setPushNotifications(originalPushNotifications);
                    }
                    handleCloseModal();
                  }}
                  sx={{
                    borderColor: '#E9EAEB',
                    color: '#414651',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (activeTab === 'Personal') {
                      handleSaveProfile();
                    } else if (activeTab === 'Security') {
                      handleSavePassword();
                    } else if (activeTab === 'Notifications') {
                      saveNotificationSettings();
                    }
                  }}
                  disabled={
                    (activeTab === 'Personal' && !hasUnsavedProfile) || // Disable if no changes in Personal
                    (activeTab === 'Security' && (!hasUnsavedPassword || isSSOLogin)) || // Disable if no changes in Security or SSO login
                    (activeTab === 'Notifications' && !hasUnsavedNotifications) || // Disable if no changes in Notifications
                    profileLoading || // Disable while saving profile
                    passwordLoading || // Disable while saving password
                    notifSaving // Disable while saving notifications
                  }
                  sx={{
                    bgcolor:
                      (activeTab === 'Personal' && hasUnsavedProfile && !profileLoading) ||
                      (activeTab === 'Security' &&
                        hasUnsavedPassword &&
                        !passwordLoading &&
                        !isSSOLogin) ||
                      (activeTab === 'Notifications' && hasUnsavedNotifications && !notifSaving)
                        ? '#617BFF'
                        : '#E5E7EB',
                    color:
                      (activeTab === 'Personal' && hasUnsavedProfile && !profileLoading) ||
                      (activeTab === 'Security' &&
                        hasUnsavedPassword &&
                        !passwordLoading &&
                        !isSSOLogin) ||
                      (activeTab === 'Notifications' && hasUnsavedNotifications && !notifSaving)
                        ? 'white'
                        : '#9CA3AF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: { xs: '13px', sm: '14px' },
                    borderRadius: '8px',
                    height: '36px',
                    flex: { xs: 1, sm: 'none' },
                    '&:hover': {
                      bgcolor:
                        (activeTab === 'Personal' && hasUnsavedProfile && !profileLoading) ||
                        (activeTab === 'Security' &&
                          hasUnsavedPassword &&
                          !passwordLoading &&
                          !isSSOLogin) ||
                        (activeTab === 'Notifications' && hasUnsavedNotifications && !notifSaving)
                          ? '#4A67E3'
                          : '#E5E7EB',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  {profileLoading || passwordLoading || notifSaving ? 'Saving...' : 'Save changes'}
                </Button>
              </Box>
            </Box>
          )}
      </DialogContent>

      {/* Unsaved Changes Confirmation Dialog */}
      <Dialog open={unsavedChangesDialog} onClose={handleCancelDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '18px', color: '#101828' }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#475467', fontSize: '14px' }}>
            You have unsaved modifications. Do you want to save your changes before leaving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleDiscardAndContinue}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderColor: '#E9EAEB',
              color: '#414651',
              '&:hover': {
                borderColor: '#D1D5DB',
                bgcolor: '#F9FAFB',
              },
            }}
          >
            No, discard
          </Button>
          <Button
            onClick={handleSaveAndContinue}
            variant="contained"
            disabled={notifSaving}
            sx={{
              textTransform: 'none',
              bgcolor: '#617BFF',
              '&:hover': {
                bgcolor: '#4F69E8',
              },
            }}
          >
            {notifSaving ? 'Saving...' : 'Yes, save'}
          </Button>
        </DialogActions>
      </Dialog>

      <UpgradePlanModal
        open={upgradePlanModalOpen}
        onClose={() => setUpgradePlanModalOpen(false)}
        currentPlan={userQuota?.subscription?.name || currentSubscription?.name}
      />
      <PayAsYouNeedModal open={payAsYouNeedOpen} onClose={() => setPayAsYouNeedOpen(false)} />
    </Dialog>
  );
};

export default ProfileModal;
